// AI Platform Data - Enhanced with detailed information for comparison and detail views
// NOTE: This is illustrative data for V1. Information may not reflect current offerings.

export const platforms = [
  {
    id: 'chatgpt',
    name: 'ChatGPT',
    provider: 'OpenAI',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/0/04/ChatGPT_logo.svg',
    summary: 'Widely-used conversational AI for writing, coding, and general assistance.',
    description: 'ChatGPT is a general-purpose AI assistant that can help with writing, answering questions, coding assistance, and creative tasks. Built on OpenAI\'s GPT models, it offers both free and paid tiers with varying capabilities.',
    
    // Detailed features for comparison
    features: {
      conversational: true,
      codeGeneration: true,
      imageGeneration: true,
      imageAnalysis: true,
      documentAnalysis: true,
      webBrowsing: true,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: true,
      voiceInput: true,
      mobileApp: true,
      teamCollaboration: true,
    },
    
    // Pricing details
    pricing: {
      hasFreeeTier: true,
      freeTierLimits: 'Limited messages per hour, GPT-3.5 only',
      paidPlans: [
        { name: 'Plus', price: '£20/month', features: 'GPT-4, DALL-E, browsing, plugins' },
        { name: 'Team', price: '£25/user/month', features: 'Higher limits, workspace features' },
        { name: 'Enterprise', price: 'Custom', features: 'Unlimited, SSO, admin controls' },
      ],
      apiPricing: 'Pay-per-token (varies by model)',
    },
    
    // Data handling details
    dataHandling: {
      level: 'clear',
      dataRetention: 'Chat history stored unless opted out',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: false,
      soc2Certified: true,
      dataLocation: 'USA (with some EU options for enterprise)',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    // Pros and cons
    pros: [
      'Large knowledge base and general capability',
      'Regular updates and improvements',
      'Wide plugin ecosystem',
      'Good at creative and coding tasks',
      'Available API for developers',
    ],
    cons: [
      'Knowledge cutoff date limitations',
      'Can produce confident but incorrect answers',
      'Free tier has significant limitations',
      'Data privacy concerns for sensitive use',
    ],
    
    // Best for / not ideal for
    bestFor: [
      'General writing and content creation',
      'Coding assistance and debugging',
      'Learning and research',
      'Creative brainstorming',
    ],
    notIdealFor: [
      'Highly sensitive or regulated data',
      'Real-time information needs',
      'Tasks requiring guaranteed accuracy',
    ],
    
    useCases: ['customer-support', 'writing-content', 'internal-docs', 'automation', 'data-analysis'],
    userTypes: ['individual', 'small-team', 'business', 'public'],
    dataSensitivity: ['public', 'business'],
    technicalLevel: ['simple', 'some-setup', 'developer'],
    pricingModel: ['free', 'subscription', 'usage-based'],
    costBand: 2,
    tags: ['conversational', 'general-purpose', 'api-available'],
    
    // Links
    links: {
      website: 'https://chat.openai.com',
      documentation: 'https://platform.openai.com/docs',
      pricing: 'https://openai.com/pricing',
    },
  },
  {
    id: 'claude',
    name: 'Claude',
    provider: 'Anthropic',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/1/17/Anthropic_logo.svg',
    summary: 'AI assistant designed with safety focus, strong at analysis and long documents.',
    description: 'Claude is an AI assistant built by Anthropic with a focus on being helpful, harmless, and honest. Particularly strong with long documents, nuanced analysis, and maintaining context across long conversations.',
    
    features: {
      conversational: true,
      codeGeneration: true,
      imageGeneration: false,
      imageAnalysis: true,
      documentAnalysis: true,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: false,
      voiceInput: false,
      mobileApp: true,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: true,
      freeTierLimits: 'Limited daily messages',
      paidPlans: [
        { name: 'Pro', price: '£20/month', features: 'Higher limits, priority access' },
        { name: 'Team', price: '£25/user/month', features: 'Collaboration features, admin' },
        { name: 'Enterprise', price: 'Custom', features: 'Custom limits, SSO, dedicated support' },
      ],
      apiPricing: 'Pay-per-token',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Conversations not used for training by default',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: false,
      soc2Certified: true,
      dataLocation: 'USA',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Strong with long documents (200K+ tokens)',
      'Thoughtful, nuanced responses',
      'Good at following complex instructions',
      'Strong ethical guardrails',
      'Clear data handling policies',
    ],
    cons: [
      'No image generation capability',
      'No web browsing in standard version',
      'Smaller plugin/integration ecosystem',
      'Can be overly cautious on some topics',
    ],
    
    bestFor: [
      'Document analysis and summarisation',
      'Complex writing and editing',
      'Research and analysis tasks',
      'Situations requiring nuanced responses',
    ],
    notIdealFor: [
      'Image generation needs',
      'Real-time web information',
      'Quick, simple queries (may be verbose)',
    ],
    
    useCases: ['customer-support', 'writing-content', 'internal-docs', 'data-analysis'],
    userTypes: ['individual', 'small-team', 'business'],
    dataSensitivity: ['public', 'business', 'personal'],
    technicalLevel: ['simple', 'some-setup', 'developer'],
    pricingModel: ['free', 'subscription', 'usage-based'],
    costBand: 2,
    tags: ['conversational', 'analysis', 'long-context', 'safety-focused'],
    
    links: {
      website: 'https://claude.ai',
      documentation: 'https://docs.anthropic.com',
      pricing: 'https://www.anthropic.com/pricing',
    },
  },
  {
    id: 'gemini',
    name: 'Gemini',
    provider: 'Google',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/8/8a/Google_Gemini_logo.svg',
    summary: 'Google\'s multimodal AI, integrated with Google Workspace and services.',
    description: 'Gemini is Google\'s AI assistant capable of understanding and generating text, images, and code. Deep integration with Google Workspace makes it powerful for productivity tasks within the Google ecosystem.',
    
    features: {
      conversational: true,
      codeGeneration: true,
      imageGeneration: true,
      imageAnalysis: true,
      documentAnalysis: true,
      webBrowsing: true,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: true,
      voiceInput: true,
      mobileApp: true,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: true,
      freeTierLimits: 'Basic features, limited usage',
      paidPlans: [
        { name: 'Advanced', price: '£19/month', features: 'Gemini Ultra, 2TB storage' },
        { name: 'Business', price: '£19/user/month', features: 'Workspace integration, admin' },
        { name: 'Enterprise', price: 'Custom', features: 'Advanced security, compliance' },
      ],
      apiPricing: 'Pay-per-token with free tier',
    },
    
    dataHandling: {
      level: 'moderate',
      dataRetention: 'Data may be used to improve services (configurable)',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: false,
      soc2Certified: true,
      dataLocation: 'Global (various regions)',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Excellent Google Workspace integration',
      'Strong multimodal capabilities',
      'Real-time information access',
      'Competitive free tier',
      'Growing feature set',
    ],
    cons: [
      'Best features require Google ecosystem',
      'Data handling policies less clear than some',
      'Newer platform, still maturing',
      'Some features region-restricted',
    ],
    
    bestFor: [
      'Google Workspace users',
      'Multimodal tasks (text + images)',
      'Research with real-time data',
      'Productivity within Google ecosystem',
    ],
    notIdealFor: [
      'Users outside Google ecosystem',
      'Highly regulated environments',
      'Tasks requiring data isolation',
    ],
    
    useCases: ['customer-support', 'writing-content', 'internal-docs', 'automation', 'data-analysis', 'image-media'],
    userTypes: ['individual', 'small-team', 'business', 'public'],
    dataSensitivity: ['public', 'business'],
    technicalLevel: ['simple', 'some-setup', 'developer'],
    pricingModel: ['free', 'subscription', 'enterprise'],
    costBand: 2,
    tags: ['multimodal', 'google-integration', 'workspace'],
    
    links: {
      website: 'https://gemini.google.com',
      documentation: 'https://ai.google.dev/docs',
      pricing: 'https://one.google.com/about/plans',
    },
  },
  {
    id: 'copilot',
    name: 'Microsoft Copilot',
    provider: 'Microsoft',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/2/2a/Microsoft_365_Copilot_Icon.svg',
    summary: 'AI assistant embedded in Microsoft 365 for productivity tasks.',
    description: 'Microsoft Copilot helps with writing, summarising, and creating content across Microsoft 365 apps including Word, Excel, PowerPoint, Outlook, and Teams. Deep integration with the Microsoft ecosystem.',
    
    features: {
      conversational: true,
      codeGeneration: true,
      imageGeneration: true,
      imageAnalysis: true,
      documentAnalysis: true,
      webBrowsing: true,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: true,
      voiceInput: true,
      mobileApp: true,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: true,
      freeTierLimits: 'Basic Copilot features',
      paidPlans: [
        { name: 'Pro', price: '£20/month', features: 'Priority access, Office integration' },
        { name: 'Microsoft 365 Copilot', price: '£24/user/month', features: 'Full M365 integration' },
        { name: 'Enterprise', price: 'Custom', features: 'Advanced compliance, support' },
      ],
      apiPricing: 'Via Azure OpenAI Service',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Enterprise data protection built-in',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: true,
      soc2Certified: true,
      dataLocation: 'Configurable by region',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Deep Microsoft 365 integration',
      'Strong enterprise security features',
      'Familiar interface for Office users',
      'Good compliance certifications',
      'Multi-platform support',
    ],
    cons: [
      'Best value requires M365 subscription',
      'Can be expensive for full features',
      'Some features still maturing',
      'Performance varies by application',
    ],
    
    bestFor: [
      'Microsoft 365 users',
      'Enterprise environments',
      'Document-heavy workflows',
      'Teams collaboration',
    ],
    notIdealFor: [
      'Non-Microsoft environments',
      'Budget-conscious individuals',
      'Standalone AI assistant needs',
    ],
    
    useCases: ['writing-content', 'internal-docs', 'automation', 'data-analysis'],
    userTypes: ['individual', 'small-team', 'business'],
    dataSensitivity: ['public', 'business', 'personal'],
    technicalLevel: ['simple', 'some-setup'],
    pricingModel: ['subscription', 'enterprise'],
    costBand: 3,
    tags: ['microsoft-integration', 'productivity', 'enterprise'],
    
    links: {
      website: 'https://copilot.microsoft.com',
      documentation: 'https://learn.microsoft.com/copilot',
      pricing: 'https://www.microsoft.com/microsoft-365/copilot',
    },
  },
  {
    id: 'perplexity',
    name: 'Perplexity AI',
    provider: 'Perplexity',
    logo: null,
    summary: 'AI-powered search engine with cited sources and real-time information.',
    description: 'Perplexity combines conversational AI with web search to provide answers with citations. Excellent for research tasks requiring up-to-date information with verifiable sources.',
    
    features: {
      conversational: true,
      codeGeneration: true,
      imageGeneration: false,
      imageAnalysis: true,
      documentAnalysis: true,
      webBrowsing: true,
      apiAccess: true,
      customInstructions: false,
      pluginsExtensions: false,
      voiceInput: true,
      mobileApp: true,
      teamCollaboration: false,
    },
    
    pricing: {
      hasFreeeTier: true,
      freeTierLimits: 'Limited Pro searches per day',
      paidPlans: [
        { name: 'Pro', price: '£20/month', features: 'Unlimited Pro searches, file uploads' },
        { name: 'Enterprise', price: 'Custom', features: 'Team features, SSO, analytics' },
      ],
      apiPricing: 'Pay-per-request',
    },
    
    dataHandling: {
      level: 'moderate',
      dataRetention: 'Search history stored',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: false,
      soc2Certified: false,
      dataLocation: 'USA',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Real-time web information',
      'Citations for fact-checking',
      'Clean, focused interface',
      'Good for research tasks',
      'Competitive free tier',
    ],
    cons: [
      'Limited creative capabilities',
      'No image generation',
      'Smaller feature set than competitors',
      'Less customisation options',
    ],
    
    bestFor: [
      'Research and fact-finding',
      'Current events and news',
      'Academic and professional research',
      'Quick information lookups',
    ],
    notIdealFor: [
      'Creative writing tasks',
      'Image generation needs',
      'Complex document workflows',
    ],
    
    useCases: ['internal-docs', 'data-analysis'],
    userTypes: ['individual', 'small-team'],
    dataSensitivity: ['public'],
    technicalLevel: ['simple'],
    pricingModel: ['free', 'subscription'],
    costBand: 1,
    tags: ['search', 'citations', 'research'],
    
    links: {
      website: 'https://perplexity.ai',
      documentation: 'https://docs.perplexity.ai',
      pricing: 'https://perplexity.ai/pro',
    },
  },
  {
    id: 'midjourney',
    name: 'Midjourney',
    provider: 'Midjourney',
    logo: null,
    summary: 'AI image generation focused on artistic and creative visuals.',
    description: 'Midjourney creates images from text descriptions, known for producing artistic, stylised, and high-quality visual outputs. Accessed primarily through Discord.',
    
    features: {
      conversational: false,
      codeGeneration: false,
      imageGeneration: true,
      imageAnalysis: false,
      documentAnalysis: false,
      webBrowsing: false,
      apiAccess: false,
      customInstructions: true,
      pluginsExtensions: false,
      voiceInput: false,
      mobileApp: false,
      teamCollaboration: false,
    },
    
    pricing: {
      hasFreeeTier: false,
      freeTierLimits: 'N/A',
      paidPlans: [
        { name: 'Basic', price: '£10/month', features: '~200 images/month' },
        { name: 'Standard', price: '£30/month', features: '~900 images/month, fast hours' },
        { name: 'Pro', price: '£60/month', features: '~1800 images/month, stealth mode' },
      ],
      apiPricing: 'Not available',
    },
    
    dataHandling: {
      level: 'moderate',
      dataRetention: 'Images stored on Midjourney servers',
      optOutAvailable: false,
      enterpriseDataProtection: false,
      gdprCompliant: true,
      hipaaCompliant: false,
      soc2Certified: false,
      dataLocation: 'USA',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Exceptional artistic quality',
      'Distinctive aesthetic styles',
      'Active community for inspiration',
      'Regular model improvements',
      'Good at creative interpretation',
    ],
    cons: [
      'Discord-only interface',
      'No free tier available',
      'Limited control over outputs',
      'No API access',
      'Images not private by default',
    ],
    
    bestFor: [
      'Artistic and creative projects',
      'Marketing and social media visuals',
      'Concept art and inspiration',
      'Stylised imagery needs',
    ],
    notIdealFor: [
      'Precise, technical imagery',
      'Private/confidential visuals',
      'Integration with other tools',
      'Budget-conscious users',
    ],
    
    useCases: ['image-media'],
    userTypes: ['individual', 'small-team'],
    dataSensitivity: ['public'],
    technicalLevel: ['simple', 'some-setup'],
    pricingModel: ['subscription'],
    costBand: 2,
    tags: ['image-generation', 'creative', 'art'],
    
    links: {
      website: 'https://midjourney.com',
      documentation: 'https://docs.midjourney.com',
      pricing: 'https://midjourney.com/account',
    },
  },
  {
    id: 'dalle',
    name: 'DALL-E',
    provider: 'OpenAI',
    logo: null,
    summary: 'AI image generation integrated with ChatGPT and available via API.',
    description: 'DALL-E generates and edits images from text descriptions. Accessible through ChatGPT Plus or directly via API, offering good integration with OpenAI\'s ecosystem.',
    
    features: {
      conversational: false,
      codeGeneration: false,
      imageGeneration: true,
      imageAnalysis: false,
      documentAnalysis: false,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: false,
      voiceInput: false,
      mobileApp: true,
      teamCollaboration: false,
    },
    
    pricing: {
      hasFreeeTier: false,
      freeTierLimits: 'Included with ChatGPT Plus',
      paidPlans: [
        { name: 'Via ChatGPT Plus', price: '£20/month', features: 'Bundled with ChatGPT' },
        { name: 'API', price: 'Pay-per-image', features: 'Direct API access' },
      ],
      apiPricing: '£0.02-0.08 per image depending on size',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Same as OpenAI policies',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: false,
      soc2Certified: true,
      dataLocation: 'USA',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Good ChatGPT integration',
      'API available for developers',
      'Image editing capabilities',
      'Clear usage policies',
      'Part of larger OpenAI ecosystem',
    ],
    cons: [
      'Requires ChatGPT Plus or API costs',
      'Style less distinctive than Midjourney',
      'Limited by safety filters',
      'Quality can be inconsistent',
    ],
    
    bestFor: [
      'ChatGPT users needing images',
      'Developers needing API access',
      'Quick image generation tasks',
      'Integration with text workflows',
    ],
    notIdealFor: [
      'Highly artistic/stylised needs',
      'High-volume image generation',
      'Standalone image tool users',
    ],
    
    useCases: ['image-media'],
    userTypes: ['individual', 'small-team', 'business'],
    dataSensitivity: ['public', 'business'],
    technicalLevel: ['simple', 'developer'],
    pricingModel: ['subscription', 'usage-based'],
    costBand: 2,
    tags: ['image-generation', 'api-available'],
    
    links: {
      website: 'https://openai.com/dall-e-3',
      documentation: 'https://platform.openai.com/docs/guides/images',
      pricing: 'https://openai.com/pricing',
    },
  },
  // Fictional platforms for demonstration
  {
    id: 'clearai-enterprise',
    name: 'ClearAI Enterprise',
    provider: 'ClearAI (Fictional)',
    logo: null,
    summary: '[Example Platform] Enterprise AI with on-premise deployment options.',
    description: 'ClearAI Enterprise is a fictional example of an enterprise-focused AI platform offering on-premise deployment, comprehensive compliance certifications, and dedicated support for large organisations.',
    
    features: {
      conversational: true,
      codeGeneration: true,
      imageGeneration: false,
      imageAnalysis: true,
      documentAnalysis: true,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: true,
      voiceInput: false,
      mobileApp: false,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: false,
      freeTierLimits: 'N/A',
      paidPlans: [
        { name: 'Enterprise', price: 'Custom (typically £50k+/year)', features: 'Full platform access' },
      ],
      apiPricing: 'Included in enterprise licence',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Customer-controlled, on-premise options',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: true,
      soc2Certified: true,
      dataLocation: 'Customer choice / on-premise',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'On-premise deployment available',
      'Comprehensive compliance (HIPAA, SOC 2)',
      'Dedicated support and SLAs',
      'Full data sovereignty',
      'Customisable to organisation needs',
    ],
    cons: [
      'High cost, enterprise pricing only',
      'Longer implementation timeline',
      'Requires IT resources to manage',
      'No free trial typically',
    ],
    
    bestFor: [
      'Large enterprises',
      'Regulated industries (healthcare, finance)',
      'Organisations requiring data sovereignty',
      'High-security environments',
    ],
    notIdealFor: [
      'Small businesses or individuals',
      'Quick deployment needs',
      'Budget-constrained projects',
    ],
    
    useCases: ['customer-support', 'internal-docs', 'automation', 'data-analysis'],
    userTypes: ['business', 'public'],
    dataSensitivity: ['public', 'business', 'personal', 'regulated'],
    technicalLevel: ['some-setup', 'developer'],
    pricingModel: ['enterprise'],
    costBand: 4,
    tags: ['enterprise', 'on-premise', 'compliant', 'fictional'],
    
    links: {
      website: '#',
      documentation: '#',
      pricing: '#',
    },
  },
  {
    id: 'simplechat',
    name: 'SimpleChat',
    provider: 'SimpleAI (Fictional)',
    logo: null,
    summary: '[Example Platform] No-code chatbot builder for small businesses.',
    description: 'SimpleChat is a fictional example of an accessible chatbot platform designed for small businesses without technical expertise. Drag-and-drop interface with pre-built templates.',
    
    features: {
      conversational: true,
      codeGeneration: false,
      imageGeneration: false,
      imageAnalysis: false,
      documentAnalysis: false,
      webBrowsing: false,
      apiAccess: false,
      customInstructions: true,
      pluginsExtensions: false,
      voiceInput: false,
      mobileApp: false,
      teamCollaboration: false,
    },
    
    pricing: {
      hasFreeeTier: true,
      freeTierLimits: '100 conversations/month',
      paidPlans: [
        { name: 'Starter', price: '£15/month', features: '1,000 conversations, basic analytics' },
        { name: 'Growth', price: '£49/month', features: '10,000 conversations, integrations' },
      ],
      apiPricing: 'Not available',
    },
    
    dataHandling: {
      level: 'moderate',
      dataRetention: 'Conversation logs stored for 90 days',
      optOutAvailable: true,
      enterpriseDataProtection: false,
      gdprCompliant: true,
      hipaaCompliant: false,
      soc2Certified: false,
      dataLocation: 'EU/USA',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Very easy to use, no coding needed',
      'Affordable pricing',
      'Quick to deploy',
      'Good for basic customer support',
      'Pre-built templates available',
    ],
    cons: [
      'Limited AI capabilities',
      'No advanced features',
      'Not suitable for complex needs',
      'Limited integration options',
    ],
    
    bestFor: [
      'Small businesses',
      'Basic FAQ chatbots',
      'Quick deployment needs',
      'Non-technical users',
    ],
    notIdealFor: [
      'Complex AI requirements',
      'Enterprise deployments',
      'Advanced customisation needs',
    ],
    
    useCases: ['customer-support'],
    userTypes: ['individual', 'small-team'],
    dataSensitivity: ['public', 'business'],
    technicalLevel: ['simple'],
    pricingModel: ['free', 'subscription'],
    costBand: 1,
    tags: ['chatbot', 'no-code', 'small-business', 'fictional'],
    
    links: {
      website: '#',
      documentation: '#',
      pricing: '#',
    },
  },
  {
    id: 'datawise',
    name: 'DataWise Analytics',
    provider: 'DataWise (Fictional)',
    logo: null,
    summary: '[Example Platform] AI analytics for business intelligence and reporting.',
    description: 'DataWise is a fictional example of a business intelligence platform using AI for automated insights, natural language queries, and intelligent reporting.',
    
    features: {
      conversational: true,
      codeGeneration: false,
      imageGeneration: false,
      imageAnalysis: false,
      documentAnalysis: true,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: true,
      voiceInput: false,
      mobileApp: true,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: false,
      freeTierLimits: 'N/A',
      paidPlans: [
        { name: 'Team', price: '£50/user/month', features: 'Core analytics, 5 data sources' },
        { name: 'Business', price: '£100/user/month', features: 'Advanced AI, unlimited sources' },
      ],
      apiPricing: 'Included in plan',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Customer data isolated per tenant',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: false,
      soc2Certified: true,
      dataLocation: 'Customer choice',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Natural language data queries',
      'Automated insight generation',
      'Good visualisation options',
      'Team collaboration features',
      'Multiple data source support',
    ],
    cons: [
      'Focused on analytics only',
      'Can be expensive for small teams',
      'Learning curve for advanced features',
      'Requires good data quality',
    ],
    
    bestFor: [
      'Business intelligence teams',
      'Data-driven organisations',
      'Report automation needs',
      'Non-technical data users',
    ],
    notIdealFor: [
      'General AI assistant needs',
      'Content creation',
      'Customer-facing applications',
    ],
    
    useCases: ['data-analysis', 'automation'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['business', 'personal'],
    technicalLevel: ['some-setup'],
    pricingModel: ['subscription', 'usage-based'],
    costBand: 3,
    tags: ['analytics', 'business-intelligence', 'fictional'],
    
    links: {
      website: '#',
      documentation: '#',
      pricing: '#',
    },
  },
  {
    id: 'secureai-gov',
    name: 'SecureAI Gov',
    provider: 'SecureAI (Fictional)',
    logo: null,
    summary: '[Example Platform] Government-grade AI with highest security certifications.',
    description: 'SecureAI Gov is a fictional example of a highly secure AI platform designed for government and regulated industries, with FedRAMP, ITAR, and other high-security certifications.',
    
    features: {
      conversational: true,
      codeGeneration: true,
      imageGeneration: false,
      imageAnalysis: true,
      documentAnalysis: true,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: false,
      voiceInput: false,
      mobileApp: false,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: false,
      freeTierLimits: 'N/A',
      paidPlans: [
        { name: 'Government', price: 'Custom (typically £100k+/year)', features: 'Full compliance suite' },
      ],
      apiPricing: 'Included in contract',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Customer-controlled, air-gapped options',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: true,
      soc2Certified: true,
      dataLocation: 'Customer-specified sovereign location',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Highest security certifications',
      'Air-gapped deployment options',
      'Full audit trails',
      'Dedicated security team',
      'Sovereign data options',
    ],
    cons: [
      'Very high cost',
      'Complex procurement process',
      'Long implementation timeline',
      'Limited feature velocity',
    ],
    
    bestFor: [
      'Government agencies',
      'Defence contractors',
      'Highly regulated industries',
      'Classified environment needs',
    ],
    notIdealFor: [
      'Commercial businesses',
      'Quick deployment needs',
      'Cost-sensitive projects',
    ],
    
    useCases: ['internal-docs', 'data-analysis'],
    userTypes: ['business'],
    dataSensitivity: ['regulated'],
    technicalLevel: ['developer'],
    pricingModel: ['enterprise'],
    costBand: 4,
    tags: ['government', 'security', 'compliant', 'fictional'],
    
    links: {
      website: '#',
      documentation: '#',
      pricing: '#',
    },
  },
  // ============================================
  // TechEx Global 2026 AI Providers
  // Initial profiles — further verification and analysis pending
  // ============================================
  
  // TIER 1: Core AI Platforms & Applied AI
  {
    id: 'salesforce-einstein',
    name: 'Salesforce Einstein',
    provider: 'Salesforce',
    logo: null,
    summary: 'AI-powered CRM capabilities integrated across the Salesforce platform for sales, service, and marketing automation.',
    description: 'Salesforce Einstein brings AI to the Salesforce ecosystem, offering predictive analytics, automated insights, and intelligent recommendations across sales, service, marketing, and commerce clouds. Initial profile — further verification and analysis pending.',
    
    features: {
      conversational: true,
      codeGeneration: false,
      imageGeneration: false,
      imageAnalysis: false,
      documentAnalysis: true,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: true,
      voiceInput: false,
      mobileApp: true,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: false,
      freeTierLimits: 'N/A',
      paidPlans: [
        { name: 'Einstein for Sales', price: 'Included with Sales Cloud Enterprise+', features: 'Lead scoring, opportunity insights' },
        { name: 'Einstein for Service', price: 'Included with Service Cloud Enterprise+', features: 'Case classification, agent assist' },
        { name: 'Einstein GPT', price: 'Add-on pricing', features: 'Generative AI capabilities' },
      ],
      apiPricing: 'Usage-based within Salesforce',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Governed by Salesforce Trust policies',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: true,
      soc2Certified: true,
      dataLocation: 'Regional data centers available',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Deep CRM integration',
      'Enterprise-grade security',
      'No-code AI capabilities',
      'Extensive partner ecosystem',
    ],
    cons: [
      'Requires Salesforce subscription',
      'Best value within Salesforce ecosystem',
      'Learning curve for advanced features',
    ],
    
    bestFor: [
      'Sales teams using Salesforce',
      'Customer service automation',
      'Marketing personalization',
      'Revenue operations',
    ],
    notIdealFor: [
      'Non-Salesforce environments',
      'Standalone AI assistant needs',
      'Budget-constrained projects',
    ],
    
    useCases: ['customer-support', 'automation', 'data-analysis'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['public', 'business', 'personal'],
    technicalLevel: ['simple', 'some-setup'],
    pricingModel: ['subscription', 'enterprise'],
    costBand: 3,
    tags: ['enterprise-ai', 'crm', 'sales-automation', 'techex-2026'],
    sourceTag: 'TechEx Global 2026',
    
    links: {
      website: 'https://www.salesforce.com/einstein',
      documentation: 'https://help.salesforce.com/einstein',
      pricing: 'https://www.salesforce.com/products/einstein/pricing/',
    },
  },
  {
    id: 'ibm-watsonx',
    name: 'IBM watsonx',
    provider: 'IBM',
    logo: null,
    summary: 'Enterprise AI and data platform for building, deploying, and governing AI models at scale.',
    description: 'IBM watsonx is a comprehensive enterprise AI platform combining watsonx.ai for model training, watsonx.data for data management, and watsonx.governance for AI governance. Initial profile — further verification and analysis pending.',
    
    features: {
      conversational: true,
      codeGeneration: true,
      imageGeneration: false,
      imageAnalysis: true,
      documentAnalysis: true,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: true,
      voiceInput: false,
      mobileApp: false,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: true,
      freeTierLimits: 'Limited free tier for exploration',
      paidPlans: [
        { name: 'watsonx.ai', price: 'Usage-based', features: 'Foundation models, prompt lab' },
        { name: 'watsonx.data', price: 'Usage-based', features: 'Data lakehouse capabilities' },
        { name: 'Enterprise', price: 'Custom', features: 'Full platform, dedicated support' },
      ],
      apiPricing: 'Token-based pricing',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Customer-controlled',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: true,
      soc2Certified: true,
      dataLocation: 'Multiple regions including EU',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Strong governance capabilities',
      'Hybrid cloud deployment',
      'Enterprise compliance focus',
      'Open-source model support',
    ],
    cons: [
      'Complexity for smaller teams',
      'Premium pricing',
      'Steeper learning curve',
    ],
    
    bestFor: [
      'Large enterprises',
      'Regulated industries',
      'Custom model development',
      'AI governance requirements',
    ],
    notIdealFor: [
      'Small teams or individuals',
      'Quick prototyping',
      'Budget-limited projects',
    ],
    
    useCases: ['internal-docs', 'automation', 'data-analysis'],
    userTypes: ['business'],
    dataSensitivity: ['public', 'business', 'personal', 'regulated'],
    technicalLevel: ['some-setup', 'developer'],
    pricingModel: ['usage-based', 'enterprise'],
    costBand: 4,
    tags: ['enterprise-ai', 'governance', 'hybrid-cloud', 'techex-2026'],
    sourceTag: 'TechEx Global 2026',
    
    links: {
      website: 'https://www.ibm.com/watsonx',
      documentation: 'https://www.ibm.com/docs/watsonx',
      pricing: 'https://www.ibm.com/watsonx/pricing',
    },
  },
  {
    id: 'blue-prism',
    name: 'SS&C Blue Prism',
    provider: 'SS&C Technologies',
    logo: null,
    summary: 'Intelligent automation platform combining RPA with AI for enterprise process automation.',
    description: 'SS&C Blue Prism provides intelligent automation through robotic process automation (RPA) enhanced with AI capabilities, enabling enterprises to automate complex business processes. Initial profile — further verification and analysis pending.',
    
    features: {
      conversational: false,
      codeGeneration: false,
      imageGeneration: false,
      imageAnalysis: true,
      documentAnalysis: true,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: true,
      voiceInput: false,
      mobileApp: false,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: false,
      freeTierLimits: 'N/A',
      paidPlans: [
        { name: 'Digital Worker', price: 'Per bot licensing', features: 'RPA automation' },
        { name: 'Enterprise', price: 'Custom', features: 'Full platform, AI capabilities' },
      ],
      apiPricing: 'Included in licensing',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'On-premise or cloud options',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: true,
      soc2Certified: true,
      dataLocation: 'Customer choice',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Strong RPA foundation',
      'Enterprise-grade security',
      'On-premise deployment option',
      'Process mining integration',
    ],
    cons: [
      'Higher cost for SMBs',
      'RPA-centric approach',
      'Implementation complexity',
    ],
    
    bestFor: [
      'Process automation',
      'Finance and HR workflows',
      'Regulated industries',
      'High-volume transactions',
    ],
    notIdealFor: [
      'Creative AI tasks',
      'Small businesses',
      'Content generation',
    ],
    
    useCases: ['automation', 'internal-docs'],
    userTypes: ['business'],
    dataSensitivity: ['public', 'business', 'personal', 'regulated'],
    technicalLevel: ['some-setup', 'developer'],
    pricingModel: ['enterprise'],
    costBand: 4,
    tags: ['intelligent-automation', 'rpa', 'enterprise', 'techex-2026'],
    sourceTag: 'TechEx Global 2026',
    
    links: {
      website: 'https://www.blueprism.com',
      documentation: 'https://portal.blueprism.com',
      pricing: 'https://www.blueprism.com/contact',
    },
  },
  {
    id: 'thoughtspot',
    name: 'ThoughtSpot',
    provider: 'ThoughtSpot',
    logo: null,
    summary: 'AI-powered analytics platform enabling natural language search and automated insights from business data.',
    description: 'ThoughtSpot provides AI-driven analytics allowing users to search data using natural language and receive automated insights. Designed for self-service BI without requiring SQL knowledge. Initial profile — further verification and analysis pending.',
    
    features: {
      conversational: true,
      codeGeneration: false,
      imageGeneration: false,
      imageAnalysis: false,
      documentAnalysis: false,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: true,
      voiceInput: false,
      mobileApp: true,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: true,
      freeTierLimits: 'Free tier for small teams',
      paidPlans: [
        { name: 'Team', price: 'Per user/month', features: 'Core analytics' },
        { name: 'Pro', price: 'Per user/month', features: 'Advanced features' },
        { name: 'Enterprise', price: 'Custom', features: 'Full platform, dedicated support' },
      ],
      apiPricing: 'Included in plans',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Configurable',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: false,
      soc2Certified: true,
      dataLocation: 'Cloud or on-premise',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Natural language search',
      'No SQL required',
      'AI-generated insights',
      'Embedded analytics options',
    ],
    cons: [
      'Data preparation needed',
      'Learning curve for admins',
      'Best with structured data',
    ],
    
    bestFor: [
      'Self-service BI',
      'Business users without SQL',
      'Data democratization',
      'Embedded analytics',
    ],
    notIdealFor: [
      'Complex data science workflows',
      'Unstructured data analysis',
      'Real-time streaming data',
    ],
    
    useCases: ['data-analysis', 'internal-docs'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['public', 'business'],
    technicalLevel: ['simple', 'some-setup'],
    pricingModel: ['free', 'subscription', 'enterprise'],
    costBand: 3,
    tags: ['analytics', 'bi', 'natural-language', 'techex-2026'],
    sourceTag: 'TechEx Global 2026',
    
    links: {
      website: 'https://www.thoughtspot.com',
      documentation: 'https://docs.thoughtspot.com',
      pricing: 'https://www.thoughtspot.com/pricing',
    },
  },
  {
    id: 'datadog-ai',
    name: 'Datadog',
    provider: 'Datadog',
    logo: null,
    summary: 'Cloud monitoring and observability platform with AI-powered anomaly detection and incident management.',
    description: 'Datadog provides cloud-scale monitoring with AI-driven features including anomaly detection, automated root cause analysis, and intelligent alerting for infrastructure, applications, and logs. Initial profile — further verification and analysis pending.',
    
    features: {
      conversational: true,
      codeGeneration: false,
      imageGeneration: false,
      imageAnalysis: false,
      documentAnalysis: false,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: true,
      voiceInput: false,
      mobileApp: true,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: true,
      freeTierLimits: 'Free tier with limited features',
      paidPlans: [
        { name: 'Pro', price: 'Per host/month', features: 'Core monitoring' },
        { name: 'Enterprise', price: 'Per host/month', features: 'Advanced features, support' },
      ],
      apiPricing: 'Based on data volume',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Configurable retention',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: true,
      soc2Certified: true,
      dataLocation: 'Multiple regions',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Comprehensive observability',
      'AI-powered insights',
      'Extensive integrations',
      'Real-time monitoring',
    ],
    cons: [
      'Cost can scale quickly',
      'Complexity for small teams',
      'Focused on DevOps/IT',
    ],
    
    bestFor: [
      'DevOps teams',
      'Cloud infrastructure monitoring',
      'Incident management',
      'Performance optimization',
    ],
    notIdealFor: [
      'Non-technical users',
      'Content creation',
      'General business AI',
    ],
    
    useCases: ['automation', 'data-analysis'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['public', 'business'],
    technicalLevel: ['some-setup', 'developer'],
    pricingModel: ['free', 'usage-based', 'enterprise'],
    costBand: 3,
    tags: ['observability', 'monitoring', 'devops', 'techex-2026'],
    sourceTag: 'TechEx Global 2026',
    
    links: {
      website: 'https://www.datadoghq.com',
      documentation: 'https://docs.datadoghq.com',
      pricing: 'https://www.datadoghq.com/pricing',
    },
  },
  {
    id: 'deepl',
    name: 'DeepL',
    provider: 'DeepL',
    logo: null,
    summary: 'AI-powered translation service known for high-quality, natural-sounding translations across multiple languages.',
    description: 'DeepL uses neural network technology to provide translations that often surpass other services in quality and naturalness. Offers both consumer and enterprise solutions. Initial profile — further verification and analysis pending.',
    
    features: {
      conversational: false,
      codeGeneration: false,
      imageGeneration: false,
      imageAnalysis: false,
      documentAnalysis: true,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: true,
      voiceInput: false,
      mobileApp: true,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: true,
      freeTierLimits: 'Limited characters per month',
      paidPlans: [
        { name: 'Pro', price: '£8/month', features: 'Unlimited translation, document translation' },
        { name: 'Team', price: '£16/user/month', features: 'Team management, glossaries' },
        { name: 'API', price: 'Usage-based', features: 'API access, custom integration' },
      ],
      apiPricing: 'Per character translated',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Texts deleted after translation (Pro)',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: false,
      soc2Certified: true,
      dataLocation: 'EU-based',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'High translation quality',
      'EU data residency',
      'Document translation',
      'Glossary support',
    ],
    cons: [
      'Limited language pairs',
      'No voice translation',
      'Premium features require subscription',
    ],
    
    bestFor: [
      'Professional translation',
      'Document localization',
      'Multi-language content',
      'EU data compliance',
    ],
    notIdealFor: [
      'Real-time interpretation',
      'Rare languages',
      'General AI assistance',
    ],
    
    useCases: ['writing-content', 'internal-docs'],
    userTypes: ['individual', 'small-team', 'business'],
    dataSensitivity: ['public', 'business'],
    technicalLevel: ['simple', 'developer'],
    pricingModel: ['free', 'subscription', 'usage-based'],
    costBand: 2,
    tags: ['translation', 'language-ai', 'localization', 'techex-2026'],
    sourceTag: 'TechEx Global 2026',
    
    links: {
      website: 'https://www.deepl.com',
      documentation: 'https://www.deepl.com/docs-api',
      pricing: 'https://www.deepl.com/pro',
    },
  },
  {
    id: 'd-id',
    name: 'D-ID',
    provider: 'D-ID',
    logo: null,
    summary: 'AI platform for creating photorealistic talking avatars and video content from text or audio.',
    description: 'D-ID specializes in generative AI for video, enabling users to create realistic talking head videos from photos and text. Used for training, marketing, and customer engagement. Initial profile — further verification and analysis pending.',
    
    features: {
      conversational: true,
      codeGeneration: false,
      imageGeneration: true,
      imageAnalysis: false,
      documentAnalysis: false,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: false,
      voiceInput: true,
      mobileApp: false,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: true,
      freeTierLimits: 'Limited minutes per month',
      paidPlans: [
        { name: 'Lite', price: '$6/month', features: '10 minutes video' },
        { name: 'Pro', price: '$50/month', features: '60 minutes, custom avatars' },
        { name: 'Enterprise', price: 'Custom', features: 'Unlimited, API, support' },
      ],
      apiPricing: 'Per minute of video',
    },
    
    dataHandling: {
      level: 'moderate',
      dataRetention: 'Videos stored per plan',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: false,
      soc2Certified: true,
      dataLocation: 'Cloud-based',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Realistic avatar generation',
      'Multi-language support',
      'No filming required',
      'Quick content creation',
    ],
    cons: [
      'Deepfake concerns',
      'Limited customization in free tier',
      'Video quality varies',
    ],
    
    bestFor: [
      'Training videos',
      'Marketing content',
      'Personalized messaging',
      'Multilingual content',
    ],
    notIdealFor: [
      'Live video applications',
      'Complex animations',
      'Highly regulated content',
    ],
    
    useCases: ['writing-content', 'image-media'],
    userTypes: ['individual', 'small-team', 'business'],
    dataSensitivity: ['public', 'business'],
    technicalLevel: ['simple', 'developer'],
    pricingModel: ['free', 'subscription', 'usage-based'],
    costBand: 2,
    tags: ['generative-ai', 'video', 'avatars', 'techex-2026'],
    sourceTag: 'TechEx Global 2026',
    
    links: {
      website: 'https://www.d-id.com',
      documentation: 'https://docs.d-id.com',
      pricing: 'https://www.d-id.com/pricing',
    },
  },
  {
    id: 'autogenai',
    name: 'AutogenAI',
    provider: 'AutogenAI',
    logo: null,
    summary: 'AI platform specializing in automated bid and proposal writing for enterprise sales teams.',
    description: 'AutogenAI uses generative AI to help organizations create compelling bids, proposals, and tender responses faster. Designed for procurement and sales teams. Initial profile — further verification and analysis pending.',
    
    features: {
      conversational: true,
      codeGeneration: false,
      imageGeneration: false,
      imageAnalysis: false,
      documentAnalysis: true,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: false,
      voiceInput: false,
      mobileApp: false,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: false,
      freeTierLimits: 'N/A',
      paidPlans: [
        { name: 'Professional', price: 'Per user/month', features: 'Core bid writing' },
        { name: 'Enterprise', price: 'Custom', features: 'Full platform, integrations' },
      ],
      apiPricing: 'Contact for pricing',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Customer-controlled',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: false,
      soc2Certified: true,
      dataLocation: 'Cloud-based',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Specialized for bids/proposals',
      'Learns from winning content',
      'Compliance-focused',
      'Time savings on tenders',
    ],
    cons: [
      'Niche use case',
      'Requires training data',
      'Enterprise pricing',
    ],
    
    bestFor: [
      'Sales proposal teams',
      'Government contractors',
      'Bid management',
      'RFP responses',
    ],
    notIdealFor: [
      'General content creation',
      'Small businesses',
      'Non-sales applications',
    ],
    
    useCases: ['writing-content', 'internal-docs'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['public', 'business'],
    technicalLevel: ['simple', 'some-setup'],
    pricingModel: ['subscription', 'enterprise'],
    costBand: 3,
    tags: ['generative-ai', 'sales', 'proposals', 'techex-2026'],
    sourceTag: 'TechEx Global 2026',
    
    links: {
      website: 'https://www.autogenai.com',
      documentation: 'https://www.autogenai.com/resources',
      pricing: 'https://www.autogenai.com/pricing',
    },
  },
  {
    id: 'glean',
    name: 'Glean',
    provider: 'Glean',
    logo: null,
    summary: 'AI-powered enterprise search platform that unifies knowledge across all company applications.',
    description: 'Glean provides AI-driven enterprise search connecting to 100+ apps to help employees find information, people, and answers across the organization. Initial profile — further verification and analysis pending.',
    
    features: {
      conversational: true,
      codeGeneration: false,
      imageGeneration: false,
      imageAnalysis: false,
      documentAnalysis: true,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: true,
      voiceInput: false,
      mobileApp: true,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: false,
      freeTierLimits: 'N/A',
      paidPlans: [
        { name: 'Enterprise', price: 'Per employee/year', features: 'Full platform access' },
      ],
      apiPricing: 'Included in enterprise',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Index only, no data storage',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: true,
      soc2Certified: true,
      dataLocation: 'Customer choice',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      '100+ app integrations',
      'Permission-aware search',
      'AI-generated answers',
      'Knowledge graph',
    ],
    cons: [
      'Enterprise pricing only',
      'Requires broad deployment',
      'Setup complexity',
    ],
    
    bestFor: [
      'Large enterprises',
      'Knowledge management',
      'Employee productivity',
      'Information discovery',
    ],
    notIdealFor: [
      'Small teams',
      'External search needs',
      'Consumer applications',
    ],
    
    useCases: ['internal-docs', 'automation'],
    userTypes: ['business'],
    dataSensitivity: ['public', 'business', 'personal'],
    technicalLevel: ['some-setup'],
    pricingModel: ['enterprise'],
    costBand: 4,
    tags: ['enterprise-search', 'knowledge-management', 'techex-2026'],
    sourceTag: 'TechEx Global 2026',
    
    links: {
      website: 'https://www.glean.com',
      documentation: 'https://help.glean.com',
      pricing: 'https://www.glean.com/pricing',
    },
  },
  {
    id: 'lilt',
    name: 'Lilt',
    provider: 'Lilt',
    logo: null,
    summary: 'AI-powered translation platform combining neural machine translation with human expertise.',
    description: 'Lilt offers enterprise translation using adaptive AI that learns from human translators, improving over time. Supports translation memory and terminology management. Initial profile — further verification and analysis pending.',
    
    features: {
      conversational: false,
      codeGeneration: false,
      imageGeneration: false,
      imageAnalysis: false,
      documentAnalysis: true,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: true,
      voiceInput: false,
      mobileApp: false,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: false,
      freeTierLimits: 'N/A',
      paidPlans: [
        { name: 'Platform', price: 'Per word', features: 'AI translation + human review' },
        { name: 'Enterprise', price: 'Custom', features: 'Full platform, dedicated support' },
      ],
      apiPricing: 'Per word translated',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Customer-controlled',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: false,
      soc2Certified: true,
      dataLocation: 'Cloud-based',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Human-in-the-loop quality',
      'Adaptive AI learning',
      'Translation memory',
      'Enterprise security',
    ],
    cons: [
      'Higher cost than pure MT',
      'Enterprise focus',
      'Setup time required',
    ],
    
    bestFor: [
      'Enterprise localization',
      'Marketing translation',
      'Technical documentation',
      'Consistent brand voice',
    ],
    notIdealFor: [
      'Quick informal translation',
      'Small projects',
      'Real-time needs',
    ],
    
    useCases: ['writing-content', 'internal-docs'],
    userTypes: ['business'],
    dataSensitivity: ['public', 'business'],
    technicalLevel: ['some-setup'],
    pricingModel: ['usage-based', 'enterprise'],
    costBand: 3,
    tags: ['translation', 'language-ai', 'localization', 'techex-2026'],
    sourceTag: 'TechEx Global 2026',
    
    links: {
      website: 'https://lilt.com',
      documentation: 'https://lilt.com/resources',
      pricing: 'https://lilt.com/pricing',
    },
  },
  
  // TIER 2: AI Infrastructure, Data & Enablement
  {
    id: 'coreweave',
    name: 'CoreWeave',
    provider: 'CoreWeave',
    logo: null,
    summary: 'Specialized cloud infrastructure provider offering GPU-optimized compute for AI and machine learning workloads.',
    description: 'CoreWeave provides cloud infrastructure specifically designed for GPU-intensive AI workloads, offering high-performance compute at competitive prices for training and inference. Initial profile — further verification and analysis pending.',
    
    features: {
      conversational: false,
      codeGeneration: false,
      imageGeneration: false,
      imageAnalysis: false,
      documentAnalysis: false,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: false,
      voiceInput: false,
      mobileApp: false,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: false,
      freeTierLimits: 'N/A',
      paidPlans: [
        { name: 'On-demand', price: 'Per GPU/hour', features: 'Flexible compute' },
        { name: 'Reserved', price: 'Discounted hourly', features: 'Committed capacity' },
        { name: 'Enterprise', price: 'Custom', features: 'Dedicated infrastructure' },
      ],
      apiPricing: 'Usage-based',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Customer-controlled',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: false,
      soc2Certified: true,
      dataLocation: 'US data centers',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Latest GPU hardware',
      'Competitive pricing',
      'AI-optimized infrastructure',
      'Kubernetes native',
    ],
    cons: [
      'Technical expertise required',
      'Limited global regions',
      'Infrastructure only',
    ],
    
    bestFor: [
      'AI model training',
      'GPU-intensive inference',
      'ML engineering teams',
      'Cost-sensitive AI compute',
    ],
    notIdealFor: [
      'Non-technical users',
      'General cloud needs',
      'Ready-made AI solutions',
    ],
    
    useCases: ['automation', 'data-analysis'],
    userTypes: ['business'],
    dataSensitivity: ['public', 'business'],
    technicalLevel: ['developer'],
    pricingModel: ['usage-based', 'enterprise'],
    costBand: 3,
    tags: ['ai-infrastructure', 'gpu-cloud', 'ml-ops', 'techex-2026'],
    sourceTag: 'TechEx Global 2026',
    
    links: {
      website: 'https://www.coreweave.com',
      documentation: 'https://docs.coreweave.com',
      pricing: 'https://www.coreweave.com/pricing',
    },
  },
  {
    id: 'rackspace',
    name: 'Rackspace Technology',
    provider: 'Rackspace',
    logo: null,
    summary: 'Managed cloud and AI services provider offering multi-cloud solutions and AI/ML managed services.',
    description: 'Rackspace provides managed cloud services including AI and machine learning solutions, helping enterprises adopt and operationalize AI across multi-cloud environments. Initial profile — further verification and analysis pending.',
    
    features: {
      conversational: false,
      codeGeneration: false,
      imageGeneration: false,
      imageAnalysis: false,
      documentAnalysis: false,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: false,
      voiceInput: false,
      mobileApp: false,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: false,
      freeTierLimits: 'N/A',
      paidPlans: [
        { name: 'Managed Services', price: 'Custom', features: 'Multi-cloud management' },
        { name: 'AI/ML Services', price: 'Custom', features: 'AI implementation, MLOps' },
      ],
      apiPricing: 'Custom based on scope',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Customer-controlled',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: true,
      soc2Certified: true,
      dataLocation: 'Multiple regions',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Multi-cloud expertise',
      'Managed AI services',
      'Enterprise support',
      'Vendor agnostic',
    ],
    cons: [
      'Higher costs',
      'Less DIY flexibility',
      'Enterprise focus only',
    ],
    
    bestFor: [
      'Multi-cloud enterprises',
      'AI adoption programs',
      'Managed ML operations',
      'Cloud migration',
    ],
    notIdealFor: [
      'Self-service needs',
      'Small businesses',
      'Quick experiments',
    ],
    
    useCases: ['automation', 'data-analysis'],
    userTypes: ['business'],
    dataSensitivity: ['public', 'business', 'personal', 'regulated'],
    technicalLevel: ['some-setup', 'developer'],
    pricingModel: ['enterprise'],
    costBand: 4,
    tags: ['managed-services', 'multi-cloud', 'mlops', 'techex-2026'],
    sourceTag: 'TechEx Global 2026',
    
    links: {
      website: 'https://www.rackspace.com',
      documentation: 'https://docs.rackspace.com',
      pricing: 'https://www.rackspace.com/solutions/ai',
    },
  },
  {
    id: 'snowflake',
    name: 'Snowflake',
    provider: 'Snowflake',
    logo: null,
    summary: 'Cloud data platform with integrated AI capabilities for data warehousing, sharing, and analytics.',
    description: 'Snowflake provides a cloud data platform with native AI/ML capabilities through Snowpark and Cortex, enabling data teams to build and deploy AI applications directly on their data. Initial profile — further verification and analysis pending.',
    
    features: {
      conversational: true,
      codeGeneration: false,
      imageGeneration: false,
      imageAnalysis: false,
      documentAnalysis: true,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: true,
      voiceInput: false,
      mobileApp: false,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: true,
      freeTierLimits: 'Trial credits',
      paidPlans: [
        { name: 'Standard', price: 'Usage-based', features: 'Core platform' },
        { name: 'Enterprise', price: 'Usage-based', features: 'Advanced security, features' },
        { name: 'Business Critical', price: 'Usage-based', features: 'HIPAA, PCI compliance' },
      ],
      apiPricing: 'Credit-based consumption',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Customer-controlled',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: true,
      soc2Certified: true,
      dataLocation: 'Multiple cloud regions',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Scalable data platform',
      'Native AI capabilities',
      'Data sharing features',
      'Multi-cloud support',
    ],
    cons: [
      'Costs can scale quickly',
      'Learning curve',
      'Data engineering focus',
    ],
    
    bestFor: [
      'Data teams',
      'AI on enterprise data',
      'Data sharing',
      'Analytics at scale',
    ],
    notIdealFor: [
      'Real-time streaming',
      'Non-data use cases',
      'Small data volumes',
    ],
    
    useCases: ['data-analysis', 'internal-docs', 'automation'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['public', 'business', 'personal', 'regulated'],
    technicalLevel: ['some-setup', 'developer'],
    pricingModel: ['usage-based', 'enterprise'],
    costBand: 3,
    tags: ['data-platform', 'analytics', 'ai-data', 'techex-2026'],
    sourceTag: 'TechEx Global 2026',
    
    links: {
      website: 'https://www.snowflake.com',
      documentation: 'https://docs.snowflake.com',
      pricing: 'https://www.snowflake.com/pricing',
    },
  },
  {
    id: 'boomi',
    name: 'Boomi',
    provider: 'Boomi',
    logo: null,
    summary: 'Integration platform as a service (iPaaS) with AI-powered data integration and automation capabilities.',
    description: 'Boomi provides cloud-native integration with AI-assisted mapping, data quality, and intelligent automation for connecting applications and data across the enterprise. Initial profile — further verification and analysis pending.',
    
    features: {
      conversational: true,
      codeGeneration: false,
      imageGeneration: false,
      imageAnalysis: false,
      documentAnalysis: false,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: true,
      voiceInput: false,
      mobileApp: false,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: true,
      freeTierLimits: 'Free tier for developers',
      paidPlans: [
        { name: 'Professional', price: 'Per connection/month', features: 'Core integration' },
        { name: 'Enterprise', price: 'Custom', features: 'Advanced features, support' },
      ],
      apiPricing: 'Included in plans',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Pass-through (no persistent storage)',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: true,
      soc2Certified: true,
      dataLocation: 'Multiple regions',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Low-code integration',
      'AI-assisted mapping',
      'Extensive connectors',
      'Master data management',
    ],
    cons: [
      'Pricing complexity',
      'Learning curve for advanced',
      'Integration focus only',
    ],
    
    bestFor: [
      'Application integration',
      'Data synchronization',
      'B2B integration',
      'API management',
    ],
    notIdealFor: [
      'AI content creation',
      'Non-integration needs',
      'Simple point-to-point',
    ],
    
    useCases: ['automation', 'internal-docs'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['public', 'business', 'personal'],
    technicalLevel: ['some-setup', 'developer'],
    pricingModel: ['free', 'subscription', 'enterprise'],
    costBand: 3,
    tags: ['ipaas', 'integration', 'automation', 'techex-2026'],
    sourceTag: 'TechEx Global 2026',
    
    links: {
      website: 'https://boomi.com',
      documentation: 'https://help.boomi.com',
      pricing: 'https://boomi.com/pricing',
    },
  },
  {
    id: 'rubrik',
    name: 'Rubrik',
    provider: 'Rubrik',
    logo: null,
    summary: 'Data security platform using AI for cyber resilience, ransomware recovery, and data governance.',
    description: 'Rubrik provides AI-driven data security including anomaly detection, threat hunting, and automated recovery capabilities for cyber resilience. Initial profile — further verification and analysis pending.',
    
    features: {
      conversational: false,
      codeGeneration: false,
      imageGeneration: false,
      imageAnalysis: false,
      documentAnalysis: true,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: true,
      voiceInput: false,
      mobileApp: true,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: false,
      freeTierLimits: 'N/A',
      paidPlans: [
        { name: 'Enterprise', price: 'Per TB managed', features: 'Core platform' },
        { name: 'Business', price: 'Custom', features: 'Advanced features' },
      ],
      apiPricing: 'Included in license',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Policy-based retention',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: true,
      soc2Certified: true,
      dataLocation: 'On-prem or cloud',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'AI-driven threat detection',
      'Ransomware recovery',
      'Data governance',
      'Multi-cloud support',
    ],
    cons: [
      'Enterprise pricing',
      'Implementation complexity',
      'Security focus only',
    ],
    
    bestFor: [
      'Cyber resilience',
      'Data backup security',
      'Ransomware protection',
      'Compliance requirements',
    ],
    notIdealFor: [
      'General AI assistance',
      'Small businesses',
      'Non-security applications',
    ],
    
    useCases: ['internal-docs', 'automation'],
    userTypes: ['business'],
    dataSensitivity: ['public', 'business', 'personal', 'regulated'],
    technicalLevel: ['some-setup', 'developer'],
    pricingModel: ['enterprise'],
    costBand: 4,
    tags: ['data-security', 'cyber-resilience', 'backup', 'techex-2026'],
    sourceTag: 'TechEx Global 2026',
    
    links: {
      website: 'https://www.rubrik.com',
      documentation: 'https://docs.rubrik.com',
      pricing: 'https://www.rubrik.com/pricing',
    },
  },
  {
    id: 'opentext',
    name: 'OpenText',
    provider: 'OpenText',
    logo: null,
    summary: 'Information management platform with AI-powered content services, automation, and analytics.',
    description: 'OpenText provides enterprise information management with AI capabilities for content analytics, intelligent capture, and business process automation. Initial profile — further verification and analysis pending.',
    
    features: {
      conversational: true,
      codeGeneration: false,
      imageGeneration: false,
      imageAnalysis: true,
      documentAnalysis: true,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: true,
      voiceInput: false,
      mobileApp: true,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: false,
      freeTierLimits: 'N/A',
      paidPlans: [
        { name: 'Cloud', price: 'Subscription', features: 'SaaS deployment' },
        { name: 'On-Premise', price: 'License + maintenance', features: 'Self-hosted' },
        { name: 'Hybrid', price: 'Custom', features: 'Mixed deployment' },
      ],
      apiPricing: 'Included in licensing',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Customer-controlled',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: true,
      soc2Certified: true,
      dataLocation: 'Multiple options',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Comprehensive ECM',
      'AI content analytics',
      'Strong compliance',
      'Extensive integrations',
    ],
    cons: [
      'Complex implementation',
      'High cost of ownership',
      'Legacy architecture aspects',
    ],
    
    bestFor: [
      'Enterprise content management',
      'Document automation',
      'Compliance-heavy industries',
      'Large-scale archiving',
    ],
    notIdealFor: [
      'Small businesses',
      'Quick deployments',
      'Modern-first requirements',
    ],
    
    useCases: ['internal-docs', 'automation', 'data-analysis'],
    userTypes: ['business'],
    dataSensitivity: ['public', 'business', 'personal', 'regulated'],
    technicalLevel: ['some-setup', 'developer'],
    pricingModel: ['subscription', 'enterprise'],
    costBand: 4,
    tags: ['ecm', 'content-management', 'automation', 'techex-2026'],
    sourceTag: 'TechEx Global 2026',
    
    links: {
      website: 'https://www.opentext.com',
      documentation: 'https://www.opentext.com/support',
      pricing: 'https://www.opentext.com/contact',
    },
  },
  {
    id: 'experian',
    name: 'Experian',
    provider: 'Experian',
    logo: null,
    summary: 'Data and analytics company offering AI-powered identity verification, fraud detection, and data quality solutions.',
    description: 'Experian provides AI-driven solutions for identity verification, fraud prevention, credit decisioning, and data quality management for financial services and beyond. Initial profile — further verification and analysis pending.',
    
    features: {
      conversational: false,
      codeGeneration: false,
      imageGeneration: false,
      imageAnalysis: false,
      documentAnalysis: true,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: false,
      voiceInput: false,
      mobileApp: false,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: false,
      freeTierLimits: 'N/A',
      paidPlans: [
        { name: 'API Services', price: 'Per transaction', features: 'Identity, fraud APIs' },
        { name: 'Enterprise', price: 'Custom', features: 'Full platform access' },
      ],
      apiPricing: 'Volume-based pricing',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Compliance-driven',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: false,
      soc2Certified: true,
      dataLocation: 'Regional compliance',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Extensive data assets',
      'Proven fraud detection',
      'Regulatory expertise',
      'Global coverage',
    ],
    cons: [
      'Financial services focus',
      'Complex pricing',
      'Integration effort',
    ],
    
    bestFor: [
      'Identity verification',
      'Fraud prevention',
      'Credit decisioning',
      'Data quality',
    ],
    notIdealFor: [
      'General AI applications',
      'Non-financial use cases',
      'Small transaction volumes',
    ],
    
    useCases: ['automation', 'data-analysis'],
    userTypes: ['business'],
    dataSensitivity: ['business', 'personal', 'regulated'],
    technicalLevel: ['some-setup', 'developer'],
    pricingModel: ['usage-based', 'enterprise'],
    costBand: 3,
    tags: ['identity', 'fraud-detection', 'data-quality', 'techex-2026'],
    sourceTag: 'TechEx Global 2026',
    
    links: {
      website: 'https://www.experian.com',
      documentation: 'https://developer.experian.com',
      pricing: 'https://www.experian.com/contact',
    },
  },
  {
    id: 'spendbase',
    name: 'Spendbase',
    provider: 'Spendbase',
    logo: null,
    summary: 'AI-powered SaaS spend management platform for tracking, optimizing, and controlling software subscriptions.',
    description: 'Spendbase uses AI to help organizations discover, track, and optimize their SaaS spending, providing visibility into shadow IT and subscription management. Initial profile — further verification and analysis pending.',
    
    features: {
      conversational: false,
      codeGeneration: false,
      imageGeneration: false,
      imageAnalysis: false,
      documentAnalysis: true,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: true,
      voiceInput: false,
      mobileApp: false,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: true,
      freeTierLimits: 'Basic discovery',
      paidPlans: [
        { name: 'Pro', price: 'Per employee/month', features: 'Full platform' },
        { name: 'Enterprise', price: 'Custom', features: 'Advanced features, support' },
      ],
      apiPricing: 'Included in plans',
    },
    
    dataHandling: {
      level: 'moderate',
      dataRetention: 'As needed for service',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: false,
      soc2Certified: true,
      dataLocation: 'Cloud-based',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Shadow IT discovery',
      'Cost optimization',
      'Easy implementation',
      'ROI visibility',
    ],
    cons: [
      'Focused use case',
      'Requires financial data access',
      'Limited AI breadth',
    ],
    
    bestFor: [
      'SaaS cost management',
      'IT procurement',
      'Finance teams',
      'Shadow IT control',
    ],
    notIdealFor: [
      'General AI needs',
      'Non-SaaS environments',
      'Content creation',
    ],
    
    useCases: ['data-analysis', 'automation'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['business'],
    technicalLevel: ['simple', 'some-setup'],
    pricingModel: ['free', 'subscription', 'enterprise'],
    costBand: 2,
    tags: ['spend-management', 'saas', 'finops', 'techex-2026'],
    sourceTag: 'TechEx Global 2026',
    
    links: {
      website: 'https://spendbase.co',
      documentation: 'https://spendbase.co/resources',
      pricing: 'https://spendbase.co/pricing',
    },
  },
  
  // TIER 3: Trust, Security & Governance
  {
    id: 'threatlocker',
    name: 'ThreatLocker',
    provider: 'ThreatLocker',
    logo: null,
    summary: 'Zero trust endpoint security platform using AI for application whitelisting and ransomware prevention.',
    description: 'ThreatLocker provides AI-enhanced endpoint security through application control, ringfencing, and storage control to prevent unauthorized software and ransomware attacks. Initial profile — further verification and analysis pending.',
    
    features: {
      conversational: false,
      codeGeneration: false,
      imageGeneration: false,
      imageAnalysis: false,
      documentAnalysis: false,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: false,
      voiceInput: false,
      mobileApp: false,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: false,
      freeTierLimits: 'N/A',
      paidPlans: [
        { name: 'Per endpoint', price: 'Monthly/annually', features: 'Full platform' },
        { name: 'MSP', price: 'Volume pricing', features: 'Multi-tenant support' },
      ],
      apiPricing: 'Included',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Log retention configurable',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: true,
      soc2Certified: true,
      dataLocation: 'Cloud-based',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Zero trust approach',
      'Ransomware prevention',
      'Low system impact',
      'MSP-friendly',
    ],
    cons: [
      'Learning curve',
      'Requires policy management',
      'Endpoint focus only',
    ],
    
    bestFor: [
      'Endpoint security',
      'Ransomware protection',
      'Zero trust initiatives',
      'MSPs/MSSPs',
    ],
    notIdealFor: [
      'Network security',
      'General AI applications',
      'Non-security needs',
    ],
    
    useCases: ['automation'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['public', 'business', 'personal', 'regulated'],
    technicalLevel: ['some-setup'],
    pricingModel: ['subscription'],
    costBand: 2,
    tags: ['endpoint-security', 'zero-trust', 'ransomware', 'techex-2026'],
    sourceTag: 'TechEx Global 2026',
    
    links: {
      website: 'https://www.threatlocker.com',
      documentation: 'https://docs.threatlocker.com',
      pricing: 'https://www.threatlocker.com/pricing',
    },
  },
  {
    id: 'darktrace',
    name: 'Darktrace',
    provider: 'Darktrace',
    logo: null,
    summary: 'AI-powered cybersecurity platform using self-learning AI for threat detection and autonomous response.',
    description: 'Darktrace uses unsupervised machine learning to detect and respond to cyber threats in real-time across cloud, email, network, and endpoint environments. Initial profile — further verification and analysis pending.',
    
    features: {
      conversational: true,
      codeGeneration: false,
      imageGeneration: false,
      imageAnalysis: false,
      documentAnalysis: false,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: true,
      voiceInput: false,
      mobileApp: true,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: false,
      freeTierLimits: 'N/A',
      paidPlans: [
        { name: 'Per device/user', price: 'Annual subscription', features: 'Platform access' },
        { name: 'Enterprise', price: 'Custom', features: 'Full deployment' },
      ],
      apiPricing: 'Included',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Configurable retention',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: true,
      soc2Certified: true,
      dataLocation: 'Regional options',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Self-learning AI',
      'Autonomous response',
      'Comprehensive coverage',
      'Real-time detection',
    ],
    cons: [
      'Premium pricing',
      'Complexity for small teams',
      'Alert tuning required',
    ],
    
    bestFor: [
      'Enterprise security',
      'SOC teams',
      'Threat detection',
      'Autonomous defense',
    ],
    notIdealFor: [
      'Small businesses',
      'Basic security needs',
      'Non-security applications',
    ],
    
    useCases: ['automation'],
    userTypes: ['business'],
    dataSensitivity: ['public', 'business', 'personal', 'regulated'],
    technicalLevel: ['some-setup', 'developer'],
    pricingModel: ['enterprise'],
    costBand: 4,
    tags: ['cybersecurity', 'threat-detection', 'ai-security', 'techex-2026'],
    sourceTag: 'TechEx Global 2026',
    
    links: {
      website: 'https://www.darktrace.com',
      documentation: 'https://customerportal.darktrace.com',
      pricing: 'https://www.darktrace.com/contact',
    },
  },
  {
    id: 'vouchsec',
    name: 'Vouchsec',
    provider: 'Vouchsec',
    logo: null,
    summary: 'AI-powered identity verification and document authentication platform for compliance and fraud prevention.',
    description: 'Vouchsec provides AI-driven identity verification, document authentication, and biometric checks for KYC/AML compliance and fraud prevention. Initial profile — further verification and analysis pending.',
    
    features: {
      conversational: false,
      codeGeneration: false,
      imageGeneration: false,
      imageAnalysis: true,
      documentAnalysis: true,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: false,
      voiceInput: false,
      mobileApp: true,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: true,
      freeTierLimits: 'Limited verifications',
      paidPlans: [
        { name: 'Pay as you go', price: 'Per verification', features: 'Core checks' },
        { name: 'Enterprise', price: 'Volume pricing', features: 'Full platform' },
      ],
      apiPricing: 'Per verification',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Compliance-driven',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: false,
      soc2Certified: true,
      dataLocation: 'Multiple regions',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Fast verification',
      'Multi-document support',
      'Biometric checks',
      'API-first design',
    ],
    cons: [
      'Specialized use case',
      'Requires integration',
      'Per-transaction costs',
    ],
    
    bestFor: [
      'KYC compliance',
      'User onboarding',
      'Fraud prevention',
      'Document verification',
    ],
    notIdealFor: [
      'General AI applications',
      'Non-verification needs',
      'Offline scenarios',
    ],
    
    useCases: ['automation'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['personal', 'regulated'],
    technicalLevel: ['developer'],
    pricingModel: ['free', 'usage-based', 'enterprise'],
    costBand: 2,
    tags: ['identity-verification', 'kyc', 'fraud-prevention', 'techex-2026'],
    sourceTag: 'TechEx Global 2026',
    
    links: {
      website: 'https://vouchsec.com',
      documentation: 'https://docs.vouchsec.com',
      pricing: 'https://vouchsec.com/pricing',
    },
  },
  {
    id: 'veribee',
    name: 'Veribee',
    provider: 'Veribee',
    logo: null,
    summary: 'AI-powered supplier verification and supply chain risk management platform.',
    description: 'Veribee uses AI to verify suppliers, assess supply chain risks, and ensure compliance across global supply networks. Initial profile — further verification and analysis pending.',
    
    features: {
      conversational: false,
      codeGeneration: false,
      imageGeneration: false,
      imageAnalysis: false,
      documentAnalysis: true,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: false,
      voiceInput: false,
      mobileApp: false,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: false,
      freeTierLimits: 'N/A',
      paidPlans: [
        { name: 'Per supplier', price: 'Annual subscription', features: 'Verification platform' },
        { name: 'Enterprise', price: 'Custom', features: 'Full access, integrations' },
      ],
      apiPricing: 'Included in plans',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Customer-controlled',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: false,
      soc2Certified: true,
      dataLocation: 'Cloud-based',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Automated verification',
      'Risk scoring',
      'Compliance tracking',
      'Supply chain visibility',
    ],
    cons: [
      'Niche application',
      'Requires supplier data',
      'Enterprise focus',
    ],
    
    bestFor: [
      'Procurement teams',
      'Supply chain management',
      'Compliance verification',
      'Supplier onboarding',
    ],
    notIdealFor: [
      'General AI applications',
      'Non-procurement needs',
      'Small businesses',
    ],
    
    useCases: ['automation', 'data-analysis'],
    userTypes: ['business'],
    dataSensitivity: ['business'],
    technicalLevel: ['some-setup'],
    pricingModel: ['subscription', 'enterprise'],
    costBand: 3,
    tags: ['supply-chain', 'verification', 'compliance', 'techex-2026'],
    sourceTag: 'TechEx Global 2026',
    
    links: {
      website: 'https://veribee.com',
      documentation: 'https://veribee.com/resources',
      pricing: 'https://veribee.com/contact',
    },
  },
  
  // TIER 4: Emerging & Specialist AI
  {
    id: 'gigaspaces',
    name: 'GigaSpaces',
    provider: 'GigaSpaces',
    logo: null,
    summary: 'In-memory computing platform with AI capabilities for real-time analytics and intelligent applications.',
    description: 'GigaSpaces provides an in-memory computing platform that enables real-time AI/ML inference, streaming analytics, and intelligent caching for mission-critical applications. Initial profile — further verification and analysis pending.',
    
    features: {
      conversational: false,
      codeGeneration: false,
      imageGeneration: false,
      imageAnalysis: false,
      documentAnalysis: false,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: true,
      voiceInput: false,
      mobileApp: false,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: true,
      freeTierLimits: 'Developer edition',
      paidPlans: [
        { name: 'Enterprise', price: 'Core-based', features: 'Full platform' },
        { name: 'Cloud', price: 'Usage-based', features: 'Managed service' },
      ],
      apiPricing: 'Included in license',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'In-memory (configurable)',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: false,
      soc2Certified: true,
      dataLocation: 'Customer choice',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Ultra-low latency',
      'Real-time ML inference',
      'High availability',
      'Flexible deployment',
    ],
    cons: [
      'Technical complexity',
      'Memory costs',
      'Specialized use cases',
    ],
    
    bestFor: [
      'Real-time decisioning',
      'Financial services',
      'IoT analytics',
      'High-performance computing',
    ],
    notIdealFor: [
      'Simple applications',
      'Batch processing',
      'Non-technical teams',
    ],
    
    useCases: ['data-analysis', 'automation'],
    userTypes: ['business'],
    dataSensitivity: ['public', 'business', 'personal'],
    technicalLevel: ['developer'],
    pricingModel: ['free', 'usage-based', 'enterprise'],
    costBand: 3,
    tags: ['in-memory', 'real-time', 'analytics', 'techex-2026'],
    sourceTag: 'TechEx Global 2026',
    
    links: {
      website: 'https://www.gigaspaces.com',
      documentation: 'https://docs.gigaspaces.com',
      pricing: 'https://www.gigaspaces.com/pricing',
    },
  },
  {
    id: 'fuzzy-labs',
    name: 'Fuzzy Labs',
    provider: 'Fuzzy Labs',
    logo: null,
    summary: 'MLOps consultancy and platform providing AI implementation services and machine learning operations tools.',
    description: 'Fuzzy Labs helps organizations implement AI through MLOps consulting, custom model development, and tooling for operationalizing machine learning. Initial profile — further verification and analysis pending.',
    
    features: {
      conversational: false,
      codeGeneration: true,
      imageGeneration: false,
      imageAnalysis: false,
      documentAnalysis: false,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: false,
      voiceInput: false,
      mobileApp: false,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: false,
      freeTierLimits: 'N/A',
      paidPlans: [
        { name: 'Consulting', price: 'Project-based', features: 'AI implementation' },
        { name: 'Retainer', price: 'Monthly', features: 'Ongoing support' },
      ],
      apiPricing: 'Project-based',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Client-controlled',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: false,
      soc2Certified: false,
      dataLocation: 'Client choice',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Hands-on expertise',
      'Custom solutions',
      'Open source friendly',
      'UK-based',
    ],
    cons: [
      'Service-based model',
      'Requires engagement',
      'Not self-service',
    ],
    
    bestFor: [
      'AI implementation projects',
      'MLOps setup',
      'Custom model development',
      'Team training',
    ],
    notIdealFor: [
      'Self-service AI',
      'Quick solutions',
      'Commodity AI needs',
    ],
    
    useCases: ['automation', 'data-analysis'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['public', 'business'],
    technicalLevel: ['developer'],
    pricingModel: ['enterprise'],
    costBand: 3,
    tags: ['mlops', 'consulting', 'ai-implementation', 'techex-2026'],
    sourceTag: 'TechEx Global 2026',
    
    links: {
      website: 'https://fuzzylabs.ai',
      documentation: 'https://fuzzylabs.ai/resources',
      pricing: 'https://fuzzylabs.ai/contact',
    },
  },
  {
    id: 'quantum-metric',
    name: 'Quantum Metric',
    provider: 'Quantum Metric',
    logo: null,
    summary: 'Digital analytics platform using AI to capture and analyze customer experiences for optimization.',
    description: 'Quantum Metric provides continuous product design through AI-powered session replay, behavioral analytics, and experience optimization insights. Initial profile — further verification and analysis pending.',
    
    features: {
      conversational: false,
      codeGeneration: false,
      imageGeneration: false,
      imageAnalysis: false,
      documentAnalysis: false,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: true,
      voiceInput: false,
      mobileApp: true,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: false,
      freeTierLimits: 'N/A',
      paidPlans: [
        { name: 'Enterprise', price: 'Custom', features: 'Full platform access' },
      ],
      apiPricing: 'Included',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Configurable',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: true,
      soc2Certified: true,
      dataLocation: 'Multiple regions',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Full session capture',
      'AI-driven insights',
      'Business impact focus',
      'Cross-platform support',
    ],
    cons: [
      'Enterprise pricing',
      'Implementation effort',
      'Data volume considerations',
    ],
    
    bestFor: [
      'Digital experience teams',
      'E-commerce optimization',
      'Product analytics',
      'Customer journey analysis',
    ],
    notIdealFor: [
      'Small websites',
      'Non-digital products',
      'Basic analytics needs',
    ],
    
    useCases: ['data-analysis', 'customer-support'],
    userTypes: ['business'],
    dataSensitivity: ['public', 'business', 'personal'],
    technicalLevel: ['some-setup'],
    pricingModel: ['enterprise'],
    costBand: 4,
    tags: ['digital-analytics', 'experience-optimization', 'techex-2026'],
    sourceTag: 'TechEx Global 2026',
    
    links: {
      website: 'https://www.quantummetric.com',
      documentation: 'https://docs.quantummetric.com',
      pricing: 'https://www.quantummetric.com/demo',
    },
  },
  {
    id: 'snapshot-ai',
    name: 'Snapshot AI',
    provider: 'Snapshot',
    logo: null,
    summary: 'AI-powered visual inspection and quality control platform for manufacturing and production.',
    description: 'Snapshot AI provides computer vision solutions for automated visual inspection, defect detection, and quality control in manufacturing environments. Initial profile — further verification and analysis pending.',
    
    features: {
      conversational: false,
      codeGeneration: false,
      imageGeneration: false,
      imageAnalysis: true,
      documentAnalysis: false,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: false,
      voiceInput: false,
      mobileApp: true,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: false,
      freeTierLimits: 'N/A',
      paidPlans: [
        { name: 'Per camera/line', price: 'Subscription', features: 'Inspection platform' },
        { name: 'Enterprise', price: 'Custom', features: 'Multi-site, support' },
      ],
      apiPricing: 'Included',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Configurable',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: false,
      soc2Certified: true,
      dataLocation: 'On-premise or cloud',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Automated inspection',
      'Defect detection',
      'Edge deployment',
      'Custom model training',
    ],
    cons: [
      'Manufacturing focus',
      'Hardware requirements',
      'Implementation complexity',
    ],
    
    bestFor: [
      'Quality control',
      'Manufacturing inspection',
      'Defect detection',
      'Production monitoring',
    ],
    notIdealFor: [
      'Non-manufacturing',
      'General AI applications',
      'Office environments',
    ],
    
    useCases: ['automation', 'data-analysis'],
    userTypes: ['business'],
    dataSensitivity: ['public', 'business'],
    technicalLevel: ['some-setup', 'developer'],
    pricingModel: ['subscription', 'enterprise'],
    costBand: 3,
    tags: ['computer-vision', 'quality-control', 'manufacturing', 'techex-2026'],
    sourceTag: 'TechEx Global 2026',
    
    links: {
      website: 'https://snapshot.ai',
      documentation: 'https://snapshot.ai/docs',
      pricing: 'https://snapshot.ai/contact',
    },
  },
  {
    id: 'timextender',
    name: 'TimeXtender',
    provider: 'TimeXtender',
    logo: null,
    summary: 'Low-code data integration platform with AI-powered automation for building data estates.',
    description: 'TimeXtender provides AI-assisted data integration and transformation, enabling organizations to build and manage data pipelines with minimal coding. Initial profile — further verification and analysis pending.',
    
    features: {
      conversational: false,
      codeGeneration: true,
      imageGeneration: false,
      imageAnalysis: false,
      documentAnalysis: false,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: true,
      voiceInput: false,
      mobileApp: false,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: true,
      freeTierLimits: 'Community edition',
      paidPlans: [
        { name: 'Professional', price: 'Per user/month', features: 'Core platform' },
        { name: 'Enterprise', price: 'Custom', features: 'Full features, support' },
      ],
      apiPricing: 'Included',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Customer-controlled',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: false,
      soc2Certified: true,
      dataLocation: 'Customer choice',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Low-code approach',
      'AI-assisted design',
      'Documentation automation',
      'Data lineage',
    ],
    cons: [
      'Data engineering focus',
      'Learning curve',
      'Microsoft ecosystem bias',
    ],
    
    bestFor: [
      'Data integration',
      'Data warehousing',
      'BI preparation',
      'Data governance',
    ],
    notIdealFor: [
      'Real-time streaming',
      'Non-data use cases',
      'Simple ETL needs',
    ],
    
    useCases: ['data-analysis', 'automation'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['public', 'business'],
    technicalLevel: ['some-setup', 'developer'],
    pricingModel: ['free', 'subscription', 'enterprise'],
    costBand: 2,
    tags: ['data-integration', 'low-code', 'data-engineering', 'techex-2026'],
    sourceTag: 'TechEx Global 2026',
    
    links: {
      website: 'https://www.timextender.com',
      documentation: 'https://support.timextender.com',
      pricing: 'https://www.timextender.com/pricing',
    },
  },
  {
    id: 'turbotic',
    name: 'Turbotic',
    provider: 'Turbotic',
    logo: null,
    summary: 'Automation management platform using AI for orchestrating and optimizing RPA and automation programs.',
    description: 'Turbotic provides AI-driven automation governance, helping organizations manage, measure, and optimize their automation investments across multiple RPA platforms. Initial profile — further verification and analysis pending.',
    
    features: {
      conversational: false,
      codeGeneration: false,
      imageGeneration: false,
      imageAnalysis: false,
      documentAnalysis: false,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: true,
      voiceInput: false,
      mobileApp: false,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: false,
      freeTierLimits: 'N/A',
      paidPlans: [
        { name: 'Per bot', price: 'Monthly/annually', features: 'Management platform' },
        { name: 'Enterprise', price: 'Custom', features: 'Full access, integrations' },
      ],
      apiPricing: 'Included',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Customer-controlled',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: false,
      soc2Certified: true,
      dataLocation: 'Cloud-based',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Multi-vendor support',
      'Automation ROI tracking',
      'Governance capabilities',
      'Bot performance insights',
    ],
    cons: [
      'Requires existing RPA',
      'Niche use case',
      'Additional layer cost',
    ],
    
    bestFor: [
      'Automation CoEs',
      'Multi-vendor RPA estates',
      'Automation governance',
      'ROI measurement',
    ],
    notIdealFor: [
      'Single bot deployments',
      'Non-RPA automation',
      'Small automation programs',
    ],
    
    useCases: ['automation'],
    userTypes: ['business'],
    dataSensitivity: ['public', 'business'],
    technicalLevel: ['some-setup'],
    pricingModel: ['subscription', 'enterprise'],
    costBand: 3,
    tags: ['automation-management', 'rpa', 'governance', 'techex-2026'],
    sourceTag: 'TechEx Global 2026',
    
    links: {
      website: 'https://turbotic.com',
      documentation: 'https://turbotic.com/resources',
      pricing: 'https://turbotic.com/pricing',
    },
  },
  {
    id: 'cultureai',
    name: 'CultureAI',
    provider: 'CultureAI',
    logo: null,
    summary: 'AI-powered human risk management platform for security awareness and behavioral change.',
    description: 'CultureAI uses AI to identify, measure, and mitigate human cyber risks through continuous monitoring and targeted interventions. Initial profile — further verification and analysis pending.',
    
    features: {
      conversational: false,
      codeGeneration: false,
      imageGeneration: false,
      imageAnalysis: false,
      documentAnalysis: false,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: true,
      voiceInput: false,
      mobileApp: false,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: false,
      freeTierLimits: 'N/A',
      paidPlans: [
        { name: 'Per employee', price: 'Annual', features: 'Risk platform' },
        { name: 'Enterprise', price: 'Custom', features: 'Full access, support' },
      ],
      apiPricing: 'Included',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Configurable',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: false,
      soc2Certified: true,
      dataLocation: 'UK/EU options',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Continuous monitoring',
      'Behavioral insights',
      'Targeted training',
      'Risk quantification',
    ],
    cons: [
      'Privacy considerations',
      'Change management needed',
      'Security focus only',
    ],
    
    bestFor: [
      'Security teams',
      'Human risk management',
      'Security culture programs',
      'Compliance requirements',
    ],
    notIdealFor: [
      'General AI applications',
      'Non-security needs',
      'Small organizations',
    ],
    
    useCases: ['automation'],
    userTypes: ['business'],
    dataSensitivity: ['business', 'personal'],
    technicalLevel: ['some-setup'],
    pricingModel: ['subscription', 'enterprise'],
    costBand: 3,
    tags: ['human-risk', 'security-awareness', 'behavioral-ai', 'techex-2026'],
    sourceTag: 'TechEx Global 2026',
    
    links: {
      website: 'https://cultureai.com',
      documentation: 'https://cultureai.com/resources',
      pricing: 'https://cultureai.com/demo',
    },
  },
  {
    id: 'neural-draft',
    name: 'Neural Draft',
    provider: 'Neural Draft',
    logo: null,
    summary: 'AI-powered legal document drafting and contract automation platform for legal teams.',
    description: 'Neural Draft uses generative AI to help legal professionals draft, review, and manage contracts and legal documents more efficiently. Initial profile — further verification and analysis pending.',
    
    features: {
      conversational: true,
      codeGeneration: false,
      imageGeneration: false,
      imageAnalysis: false,
      documentAnalysis: true,
      webBrowsing: false,
      apiAccess: true,
      customInstructions: true,
      pluginsExtensions: false,
      voiceInput: false,
      mobileApp: false,
      teamCollaboration: true,
    },
    
    pricing: {
      hasFreeeTier: true,
      freeTierLimits: 'Limited documents',
      paidPlans: [
        { name: 'Professional', price: 'Per user/month', features: 'Full drafting' },
        { name: 'Enterprise', price: 'Custom', features: 'Custom templates, integrations' },
      ],
      apiPricing: 'Contact for pricing',
    },
    
    dataHandling: {
      level: 'clear',
      dataRetention: 'Customer-controlled',
      optOutAvailable: true,
      enterpriseDataProtection: true,
      gdprCompliant: true,
      hipaaCompliant: false,
      soc2Certified: true,
      dataLocation: 'Cloud-based',
      encryptionAtRest: true,
      encryptionInTransit: true,
    },
    
    pros: [
      'Legal-specific AI',
      'Template libraries',
      'Clause suggestions',
      'Time savings',
    ],
    cons: [
      'Legal review still needed',
      'Learning curve',
      'Industry-specific',
    ],
    
    bestFor: [
      'Legal teams',
      'Contract management',
      'Legal document drafting',
      'In-house counsel',
    ],
    notIdealFor: [
      'Non-legal documents',
      'Complex litigation',
      'General writing needs',
    ],
    
    useCases: ['writing-content', 'internal-docs'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['business', 'personal'],
    technicalLevel: ['simple', 'some-setup'],
    pricingModel: ['free', 'subscription', 'enterprise'],
    costBand: 2,
    tags: ['legal-tech', 'contract-automation', 'generative-ai', 'techex-2026'],
    sourceTag: 'TechEx Global 2026',
    
    links: {
      website: 'https://neuraldraft.com',
      documentation: 'https://neuraldraft.com/help',
      pricing: 'https://neuraldraft.com/pricing',
    },
  },
  
  // ============================================
  // TechEx Global 2026 - Wave 2 (50 Providers)
  // Initial profiles for discoverability
  // ============================================
  
  // === A. Applied AI & Generative AI ===
  {
    id: 'selectzero',
    name: 'SelectZero',
    provider: 'SelectZero',
    logo: null,
    summary: 'AI-powered procurement and supplier selection platform for enterprise sourcing decisions.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'AI-Enabled Platform',
    features: { conversational: false, apiAccess: true, documentAnalysis: true, teamCollaboration: true },
    pricing: { hasFreeeTier: false, paidPlans: [{ name: 'Enterprise', price: 'Custom', features: 'Full platform' }] },
    dataHandling: { level: 'moderate', gdprCompliant: true, soc2Certified: true, dataLocation: 'Cloud' },
    pros: ['Procurement automation', 'Supplier analytics'],
    cons: ['Enterprise focus'],
    bestFor: ['Procurement teams', 'Supplier management'],
    notIdealFor: ['Small businesses'],
    useCases: ['automation', 'data-analysis'],
    userTypes: ['business'],
    dataSensitivity: ['business'],
    technicalLevel: ['some-setup'],
    pricingModel: ['enterprise'],
    costBand: 3,
    tags: ['procurement', 'ai-enabled', 'enterprise', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://selectzero.ai' },
  },
  {
    id: 'synthesia',
    name: 'Synthesia',
    provider: 'Synthesia',
    logo: null,
    summary: 'AI video generation platform creating professional videos from text with realistic AI avatars.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'Core AI',
    features: { conversational: false, imageGeneration: true, voiceInput: true, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: true, paidPlans: [{ name: 'Starter', price: '$22/month', features: '10 minutes/month' }, { name: 'Creator', price: '$67/month', features: '30 minutes/month' }] },
    dataHandling: { level: 'moderate', gdprCompliant: true, soc2Certified: true, dataLocation: 'EU/US' },
    pros: ['Realistic AI avatars', 'Multi-language support', 'No filming required'],
    cons: ['Learning curve', 'Credit-based'],
    bestFor: ['Training videos', 'Marketing content', 'Corporate communications'],
    notIdealFor: ['Live video', 'Complex animations'],
    useCases: ['writing-content', 'image-media'],
    userTypes: ['individual', 'small-team', 'business'],
    dataSensitivity: ['public', 'business'],
    technicalLevel: ['simple', 'some-setup'],
    pricingModel: ['free', 'subscription'],
    costBand: 2,
    tags: ['video-generation', 'generative-ai', 'avatars', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://www.synthesia.io' },
  },
  {
    id: 'jasper-ai',
    name: 'Jasper',
    provider: 'Jasper AI',
    logo: null,
    summary: 'AI content platform for marketing teams to create on-brand content at scale.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'Core AI',
    features: { conversational: true, codeGeneration: false, imageGeneration: true, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: true, paidPlans: [{ name: 'Creator', price: '$49/month', features: 'Unlimited words' }, { name: 'Pro', price: '$69/month', features: 'Brand voice, SEO' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, soc2Certified: true, dataLocation: 'USA' },
    pros: ['Brand voice training', 'Marketing templates', 'Team collaboration'],
    cons: ['Marketing focus', 'Premium pricing'],
    bestFor: ['Marketing teams', 'Content creators', 'Agencies'],
    notIdealFor: ['Technical writing', 'Code generation'],
    useCases: ['writing-content'],
    userTypes: ['individual', 'small-team', 'business'],
    dataSensitivity: ['public', 'business'],
    technicalLevel: ['simple'],
    pricingModel: ['free', 'subscription'],
    costBand: 2,
    tags: ['content-generation', 'generative-ai', 'marketing', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://www.jasper.ai' },
  },
  {
    id: 'leena-ai',
    name: 'Leena AI',
    provider: 'Leena AI',
    logo: null,
    summary: 'AI-powered employee experience platform automating HR queries and workflows.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'AI-Enabled Platform',
    features: { conversational: true, documentAnalysis: true, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: false, paidPlans: [{ name: 'Enterprise', price: 'Custom', features: 'Full platform' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, soc2Certified: true, dataLocation: 'Multiple regions' },
    pros: ['HR automation', 'Employee self-service', 'Multi-language'],
    cons: ['Enterprise pricing', 'HR focus only'],
    bestFor: ['HR departments', 'Large enterprises', 'Employee support'],
    notIdealFor: ['Small teams', 'Non-HR use cases'],
    useCases: ['customer-support', 'automation'],
    userTypes: ['business'],
    dataSensitivity: ['business', 'personal'],
    technicalLevel: ['some-setup'],
    pricingModel: ['enterprise'],
    costBand: 3,
    tags: ['hr-tech', 'employee-experience', 'conversational-ai', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://leena.ai' },
  },
  {
    id: 'everworker',
    name: 'EverWorker',
    provider: 'EverWorker',
    logo: null,
    summary: 'AI digital workforce platform providing autonomous AI agents for business process automation.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'Intelligent Automation',
    features: { conversational: true, documentAnalysis: true, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: false, paidPlans: [{ name: 'Professional', price: 'Per agent', features: 'AI workers' }] },
    dataHandling: { level: 'moderate', gdprCompliant: true, soc2Certified: true, dataLocation: 'Cloud' },
    pros: ['Autonomous agents', 'Process automation', 'Scalable workforce'],
    cons: ['New platform', 'Enterprise focus'],
    bestFor: ['Process automation', 'Back-office operations', 'Repetitive tasks'],
    notIdealFor: ['Creative work', 'Small projects'],
    useCases: ['automation'],
    userTypes: ['business'],
    dataSensitivity: ['business'],
    technicalLevel: ['some-setup'],
    pricingModel: ['subscription', 'enterprise'],
    costBand: 3,
    tags: ['digital-workforce', 'intelligent-automation', 'ai-agents', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://everworker.ai' },
  },
  {
    id: 'zerberus-ai',
    name: 'Zerberus.ai',
    provider: 'Zerberus',
    logo: null,
    summary: 'AI-powered cybersecurity platform for threat detection and automated incident response.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'Core AI',
    features: { conversational: false, documentAnalysis: true, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: false, paidPlans: [{ name: 'Enterprise', price: 'Custom', features: 'Full security suite' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, soc2Certified: true, dataLocation: 'EU' },
    pros: ['Automated threat response', 'AI-driven detection', 'EU-based'],
    cons: ['Security focus only', 'Enterprise pricing'],
    bestFor: ['Security teams', 'Enterprise SOCs', 'Threat detection'],
    notIdealFor: ['Small businesses', 'Non-security needs'],
    useCases: ['automation'],
    userTypes: ['business'],
    dataSensitivity: ['business', 'regulated'],
    technicalLevel: ['some-setup', 'developer'],
    pricingModel: ['enterprise'],
    costBand: 4,
    tags: ['cybersecurity', 'threat-detection', 'ai-security', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://zerberus.ai' },
  },
  {
    id: 'optimizegeo-ai',
    name: 'OptimizeGEO.ai',
    provider: 'OptimizeGEO',
    logo: null,
    summary: 'AI-powered SEO and content optimization platform for improving search visibility.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'AI-Enabled Platform',
    features: { conversational: true, codeGeneration: false, documentAnalysis: true, apiAccess: true },
    pricing: { hasFreeeTier: true, paidPlans: [{ name: 'Pro', price: '$99/month', features: 'Advanced SEO' }] },
    dataHandling: { level: 'moderate', gdprCompliant: true, dataLocation: 'Cloud' },
    pros: ['SEO automation', 'Content optimization', 'Keyword research'],
    cons: ['SEO focus only', 'Learning curve'],
    bestFor: ['SEO teams', 'Content marketers', 'Agencies'],
    notIdealFor: ['Non-marketing needs', 'Technical SEO experts'],
    useCases: ['writing-content', 'data-analysis'],
    userTypes: ['individual', 'small-team', 'business'],
    dataSensitivity: ['public'],
    technicalLevel: ['simple', 'some-setup'],
    pricingModel: ['free', 'subscription'],
    costBand: 2,
    tags: ['seo', 'content-optimization', 'marketing', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://optimizegeo.ai' },
  },
  {
    id: 'supercharged-ai',
    name: 'Supercharged',
    provider: 'Supercharged',
    logo: null,
    summary: 'AI productivity platform combining multiple AI tools for enhanced workplace efficiency.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'AI-Enabled Platform',
    features: { conversational: true, codeGeneration: true, documentAnalysis: true, teamCollaboration: true },
    pricing: { hasFreeeTier: true, paidPlans: [{ name: 'Pro', price: '$20/month', features: 'Full access' }] },
    dataHandling: { level: 'moderate', gdprCompliant: true, dataLocation: 'Cloud' },
    pros: ['All-in-one AI tools', 'Productivity focus', 'Easy to use'],
    cons: ['Jack of all trades', 'Less specialized'],
    bestFor: ['Knowledge workers', 'Small teams', 'General productivity'],
    notIdealFor: ['Specialized workflows', 'Enterprise needs'],
    useCases: ['writing-content', 'automation'],
    userTypes: ['individual', 'small-team'],
    dataSensitivity: ['public', 'business'],
    technicalLevel: ['simple'],
    pricingModel: ['free', 'subscription'],
    costBand: 2,
    tags: ['productivity', 'ai-tools', 'applied-ai', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://supercharged.ai' },
  },
  {
    id: 'automation-anywhere',
    name: 'Automation Anywhere',
    provider: 'Automation Anywhere',
    logo: null,
    summary: 'Enterprise intelligent automation platform combining RPA with AI for end-to-end process automation.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'Intelligent Automation',
    features: { conversational: true, documentAnalysis: true, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: true, paidPlans: [{ name: 'Enterprise', price: 'Custom', features: 'Full platform' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, hipaaCompliant: true, soc2Certified: true, dataLocation: 'Multiple regions' },
    pros: ['Market leader', 'Enterprise-grade', 'AI + RPA combined'],
    cons: ['Complex pricing', 'Learning curve'],
    bestFor: ['Enterprise automation', 'Process optimization', 'Large organizations'],
    notIdealFor: ['Simple automations', 'Small businesses'],
    useCases: ['automation'],
    userTypes: ['business'],
    dataSensitivity: ['business', 'personal', 'regulated'],
    technicalLevel: ['some-setup', 'developer'],
    pricingModel: ['free', 'enterprise'],
    costBand: 4,
    tags: ['rpa', 'intelligent-automation', 'enterprise', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://www.automationanywhere.com' },
  },
  {
    id: 'fivetran',
    name: 'Fivetran',
    provider: 'Fivetran',
    logo: null,
    summary: 'Automated data integration platform with AI-powered data pipeline management.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'AI-Enabled Platform',
    features: { conversational: false, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: true, paidPlans: [{ name: 'Starter', price: 'Usage-based', features: 'Core connectors' }, { name: 'Enterprise', price: 'Custom', features: 'All features' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, hipaaCompliant: true, soc2Certified: true, dataLocation: 'Multiple regions' },
    pros: ['Automated pipelines', '500+ connectors', 'Low maintenance'],
    cons: ['Usage-based pricing', 'Data engineering focus'],
    bestFor: ['Data teams', 'Analytics pipelines', 'Data warehousing'],
    notIdealFor: ['Real-time streaming', 'Non-data use cases'],
    useCases: ['data-analysis', 'automation'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['business', 'personal'],
    technicalLevel: ['some-setup'],
    pricingModel: ['free', 'usage-based', 'enterprise'],
    costBand: 3,
    tags: ['data-integration', 'etl', 'data-pipeline', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://www.fivetran.com' },
  },
  
  // === B. AI Analytics, Search & Decision Intelligence ===
  {
    id: 'amplitude',
    name: 'Amplitude',
    provider: 'Amplitude',
    logo: null,
    summary: 'AI-powered product analytics platform for understanding user behavior and driving growth.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'AI-Enabled Platform',
    features: { conversational: true, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: true, paidPlans: [{ name: 'Plus', price: 'From $49/month', features: 'Advanced analytics' }, { name: 'Enterprise', price: 'Custom', features: 'Full platform' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, soc2Certified: true, dataLocation: 'USA/EU' },
    pros: ['AI-driven insights', 'Self-service analytics', 'Product focus'],
    cons: ['Learning curve', 'Can be expensive at scale'],
    bestFor: ['Product teams', 'Growth teams', 'User analytics'],
    notIdealFor: ['Marketing analytics', 'Financial reporting'],
    useCases: ['data-analysis'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['business', 'personal'],
    technicalLevel: ['some-setup'],
    pricingModel: ['free', 'subscription', 'enterprise'],
    costBand: 3,
    tags: ['product-analytics', 'user-behavior', 'ai-analytics', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://amplitude.com' },
  },
  {
    id: 'nextgenlytics',
    name: 'Nextgenlytics',
    provider: 'Nextgenlytics',
    logo: null,
    summary: 'AI-powered business intelligence platform for automated insights and reporting.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'AI-Enabled Platform',
    features: { conversational: true, documentAnalysis: true, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: false, paidPlans: [{ name: 'Professional', price: 'Contact', features: 'BI platform' }] },
    dataHandling: { level: 'moderate', gdprCompliant: true, dataLocation: 'Cloud' },
    pros: ['Automated insights', 'Natural language queries', 'Easy visualization'],
    cons: ['Newer platform', 'Limited documentation'],
    bestFor: ['Business analysts', 'Data teams', 'Reporting'],
    notIdealFor: ['Technical data science', 'Real-time analytics'],
    useCases: ['data-analysis'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['business'],
    technicalLevel: ['simple', 'some-setup'],
    pricingModel: ['subscription'],
    costBand: 2,
    tags: ['business-intelligence', 'ai-analytics', 'reporting', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://nextgenlytics.com' },
  },
  {
    id: 'kpi-tree',
    name: 'KPI Tree',
    provider: 'KPI Tree',
    logo: null,
    summary: 'AI-driven decision intelligence platform for visualizing and optimizing business metrics.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'AI-Enabled Platform',
    features: { conversational: false, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: true, paidPlans: [{ name: 'Pro', price: 'Contact', features: 'Full platform' }] },
    dataHandling: { level: 'moderate', gdprCompliant: true, dataLocation: 'Cloud' },
    pros: ['KPI visualization', 'Decision trees', 'Goal tracking'],
    cons: ['Specialized use case', 'Limited integrations'],
    bestFor: ['Business strategy', 'KPI management', 'Goal setting'],
    notIdealFor: ['Operational analytics', 'Real-time monitoring'],
    useCases: ['data-analysis'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['business'],
    technicalLevel: ['simple'],
    pricingModel: ['free', 'subscription'],
    costBand: 2,
    tags: ['decision-intelligence', 'kpi-management', 'strategy', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://kpitree.com' },
  },
  {
    id: 'aquant',
    name: 'Aquant',
    provider: 'Aquant',
    logo: null,
    summary: 'AI-powered service intelligence platform for field service and customer support optimization.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'Core AI',
    features: { conversational: true, documentAnalysis: true, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: false, paidPlans: [{ name: 'Enterprise', price: 'Custom', features: 'Service AI' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, soc2Certified: true, dataLocation: 'Cloud' },
    pros: ['Service optimization', 'Predictive maintenance', 'Knowledge capture'],
    cons: ['Industry-specific', 'Enterprise pricing'],
    bestFor: ['Field service', 'Customer support', 'Equipment maintenance'],
    notIdealFor: ['General analytics', 'Non-service industries'],
    useCases: ['customer-support', 'automation'],
    userTypes: ['business'],
    dataSensitivity: ['business'],
    technicalLevel: ['some-setup'],
    pricingModel: ['enterprise'],
    costBand: 3,
    tags: ['service-intelligence', 'field-service', 'ai-support', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://aquant.ai' },
  },
  {
    id: 'castlepoint-systems',
    name: 'Castlepoint Systems',
    provider: 'Castlepoint',
    logo: null,
    summary: 'AI-powered information governance platform for compliance, discovery, and records management.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'AI-Enabled Platform',
    features: { conversational: false, documentAnalysis: true, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: false, paidPlans: [{ name: 'Enterprise', price: 'Custom', features: 'Governance platform' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, soc2Certified: true, dataLocation: 'Multiple regions' },
    pros: ['Automated compliance', 'Records management', 'eDiscovery'],
    cons: ['Governance focus', 'Complex implementation'],
    bestFor: ['Legal teams', 'Compliance officers', 'Records management'],
    notIdealFor: ['Small businesses', 'Non-regulated industries'],
    useCases: ['internal-docs', 'automation'],
    userTypes: ['business'],
    dataSensitivity: ['business', 'regulated'],
    technicalLevel: ['some-setup'],
    pricingModel: ['enterprise'],
    costBand: 3,
    tags: ['governance', 'compliance', 'records-management', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://castlepoint.systems' },
  },
  {
    id: 'lettria',
    name: 'Lettria',
    provider: 'Lettria',
    logo: null,
    summary: 'AI-powered natural language processing platform for text analysis and understanding.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'Core AI',
    features: { conversational: false, documentAnalysis: true, apiAccess: true },
    pricing: { hasFreeeTier: true, paidPlans: [{ name: 'Pro', price: 'Usage-based', features: 'NLP API' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, dataLocation: 'EU' },
    pros: ['Advanced NLP', 'EU-based', 'Multi-language'],
    cons: ['Technical focus', 'API-only'],
    bestFor: ['Text analysis', 'NLP development', 'Document processing'],
    notIdealFor: ['Non-technical users', 'Simple use cases'],
    useCases: ['data-analysis', 'internal-docs'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['business'],
    technicalLevel: ['developer'],
    pricingModel: ['free', 'usage-based'],
    costBand: 2,
    tags: ['nlp', 'text-analysis', 'language-ai', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://www.lettria.com' },
  },
  {
    id: 'text-global',
    name: 'Text Global',
    provider: 'Text Global',
    logo: null,
    summary: 'AI-powered communications platform for intelligent messaging and customer engagement.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'AI-Enabled Platform',
    features: { conversational: true, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: false, paidPlans: [{ name: 'Business', price: 'Contact', features: 'Messaging platform' }] },
    dataHandling: { level: 'moderate', gdprCompliant: true, dataLocation: 'Cloud' },
    pros: ['Multi-channel messaging', 'AI personalization', 'Global reach'],
    cons: ['Communications focus', 'Volume-based pricing'],
    bestFor: ['Marketing teams', 'Customer engagement', 'Notifications'],
    notIdealFor: ['Internal communications', 'Non-messaging needs'],
    useCases: ['customer-support', 'automation'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['business', 'personal'],
    technicalLevel: ['some-setup'],
    pricingModel: ['subscription'],
    costBand: 2,
    tags: ['communications', 'messaging', 'customer-engagement', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://textglobal.com' },
  },
  {
    id: 'proglly',
    name: 'Proglly',
    provider: 'Proglly',
    logo: null,
    summary: 'AI-powered development analytics platform for engineering team insights and productivity.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'AI-Enabled Platform',
    features: { conversational: false, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: true, paidPlans: [{ name: 'Team', price: 'Per user', features: 'Dev analytics' }] },
    dataHandling: { level: 'moderate', gdprCompliant: true, dataLocation: 'Cloud' },
    pros: ['Developer productivity', 'Team insights', 'Git integration'],
    cons: ['Dev-focused', 'Requires code access'],
    bestFor: ['Engineering managers', 'Dev teams', 'Productivity tracking'],
    notIdealFor: ['Non-engineering teams', 'No-code environments'],
    useCases: ['data-analysis'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['business'],
    technicalLevel: ['developer'],
    pricingModel: ['free', 'subscription'],
    costBand: 2,
    tags: ['dev-analytics', 'engineering', 'productivity', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://proglly.com' },
  },
  {
    id: 'ravendb',
    name: 'RavenDB',
    provider: 'RavenDB',
    logo: null,
    summary: 'AI-ready NoSQL database with built-in machine learning capabilities for intelligent data management.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'AI-Enabled Platform',
    features: { conversational: false, apiAccess: true },
    pricing: { hasFreeeTier: true, paidPlans: [{ name: 'Professional', price: 'Per instance', features: 'Production use' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, soc2Certified: true, dataLocation: 'Customer choice' },
    pros: ['ACID transactions', 'Built-in AI', 'Multi-model'],
    cons: ['Database focus', 'Technical expertise needed'],
    bestFor: ['Application development', 'AI/ML workloads', 'Document storage'],
    notIdealFor: ['Simple use cases', 'Non-developers'],
    useCases: ['data-analysis'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['business', 'personal'],
    technicalLevel: ['developer'],
    pricingModel: ['free', 'subscription', 'enterprise'],
    costBand: 2,
    tags: ['database', 'nosql', 'ai-ready', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://ravendb.net' },
  },
  {
    id: 'redis-ai',
    name: 'Redis',
    provider: 'Redis Ltd',
    logo: null,
    summary: 'High-performance in-memory database with AI/ML capabilities for real-time applications.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'AI-Enabled Platform',
    features: { conversational: false, apiAccess: true },
    pricing: { hasFreeeTier: true, paidPlans: [{ name: 'Cloud', price: 'Usage-based', features: 'Managed Redis' }, { name: 'Enterprise', price: 'Custom', features: 'Full features' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, soc2Certified: true, dataLocation: 'Multiple regions' },
    pros: ['Ultra-fast', 'Vector search', 'AI modules'],
    cons: ['Technical expertise', 'Memory costs'],
    bestFor: ['Real-time AI', 'Caching', 'Vector databases'],
    notIdealFor: ['Non-technical users', 'Simple storage'],
    useCases: ['data-analysis', 'automation'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['business', 'personal'],
    technicalLevel: ['developer'],
    pricingModel: ['free', 'usage-based', 'enterprise'],
    costBand: 2,
    tags: ['database', 'in-memory', 'real-time', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://redis.com' },
  },
  
  // === C. Intelligent Automation & Workflow AI ===
  {
    id: 'retool',
    name: 'Retool',
    provider: 'Retool',
    logo: null,
    summary: 'Low-code platform for building internal tools with AI-powered components and automation.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'AI-Enabled Platform',
    features: { conversational: true, codeGeneration: true, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: true, paidPlans: [{ name: 'Team', price: '$10/user/month', features: 'Core features' }, { name: 'Enterprise', price: 'Custom', features: 'Full platform' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, soc2Certified: true, hipaaCompliant: true, dataLocation: 'Multiple regions' },
    pros: ['Rapid development', 'AI components', 'Many integrations'],
    cons: ['Internal tools focus', 'Some coding needed'],
    bestFor: ['Internal tools', 'Admin panels', 'Data apps'],
    notIdealFor: ['Consumer apps', 'Complex UIs'],
    useCases: ['automation', 'internal-docs'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['business', 'personal'],
    technicalLevel: ['some-setup', 'developer'],
    pricingModel: ['free', 'subscription', 'enterprise'],
    costBand: 2,
    tags: ['low-code', 'internal-tools', 'ai-components', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://retool.com' },
  },
  {
    id: 'whatfix',
    name: 'Whatfix',
    provider: 'Whatfix',
    logo: null,
    summary: 'AI-powered digital adoption platform for in-app guidance and user onboarding.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'AI-Enabled Platform',
    features: { conversational: true, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: false, paidPlans: [{ name: 'Enterprise', price: 'Custom', features: 'Full platform' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, soc2Certified: true, dataLocation: 'Multiple regions' },
    pros: ['In-app guidance', 'Analytics', 'Multi-platform'],
    cons: ['Enterprise pricing', 'Implementation time'],
    bestFor: ['Software training', 'User onboarding', 'Change management'],
    notIdealFor: ['Small apps', 'Simple workflows'],
    useCases: ['automation'],
    userTypes: ['business'],
    dataSensitivity: ['business'],
    technicalLevel: ['some-setup'],
    pricingModel: ['enterprise'],
    costBand: 3,
    tags: ['digital-adoption', 'user-onboarding', 'training', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://whatfix.com' },
  },
  {
    id: 'ortom8',
    name: 'Ortom8 Ltd',
    provider: 'Ortom8',
    logo: null,
    summary: 'AI-driven automation consultancy and platform for business process optimization.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'Intelligent Automation',
    features: { conversational: false, documentAnalysis: true, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: false, paidPlans: [{ name: 'Consulting', price: 'Project-based', features: 'Automation services' }] },
    dataHandling: { level: 'moderate', gdprCompliant: true, dataLocation: 'UK/EU' },
    pros: ['Expert guidance', 'Custom solutions', 'UK-based'],
    cons: ['Service model', 'Not self-service'],
    bestFor: ['Process automation', 'Digital transformation', 'UK businesses'],
    notIdealFor: ['DIY automation', 'Quick implementations'],
    useCases: ['automation'],
    userTypes: ['business'],
    dataSensitivity: ['business'],
    technicalLevel: ['some-setup'],
    pricingModel: ['enterprise'],
    costBand: 3,
    tags: ['automation-consulting', 'process-optimization', 'uk', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://ortom8.com' },
  },
  {
    id: 'expeero',
    name: 'Expeero',
    provider: 'Expeero',
    logo: null,
    summary: 'AI-powered workflow automation platform for enterprise process management.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'Intelligent Automation',
    features: { conversational: false, documentAnalysis: true, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: false, paidPlans: [{ name: 'Enterprise', price: 'Custom', features: 'Workflow platform' }] },
    dataHandling: { level: 'moderate', gdprCompliant: true, dataLocation: 'Cloud' },
    pros: ['Enterprise workflows', 'Process automation', 'Integration-ready'],
    cons: ['Enterprise focus', 'Implementation needed'],
    bestFor: ['Business processes', 'Workflow automation', 'Enterprise operations'],
    notIdealFor: ['Simple workflows', 'Small teams'],
    useCases: ['automation'],
    userTypes: ['business'],
    dataSensitivity: ['business'],
    technicalLevel: ['some-setup'],
    pricingModel: ['enterprise'],
    costBand: 3,
    tags: ['workflow', 'process-automation', 'enterprise', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://expeero.com' },
  },
  {
    id: 'gxpmanager',
    name: 'GxpManager',
    provider: 'GxpManager',
    logo: null,
    summary: 'AI-powered compliance management platform for regulated industries (GxP, FDA, EMA).',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'AI-Enabled Platform',
    features: { conversational: false, documentAnalysis: true, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: false, paidPlans: [{ name: 'Enterprise', price: 'Custom', features: 'Compliance platform' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, hipaaCompliant: true, soc2Certified: true, dataLocation: 'EU/US' },
    pros: ['Regulatory compliance', 'Audit trails', 'Industry-specific'],
    cons: ['Regulated industries only', 'Complex setup'],
    bestFor: ['Pharma', 'Biotech', 'Medical devices', 'Life sciences'],
    notIdealFor: ['Non-regulated industries', 'Small businesses'],
    useCases: ['internal-docs', 'automation'],
    userTypes: ['business'],
    dataSensitivity: ['regulated'],
    technicalLevel: ['some-setup'],
    pricingModel: ['enterprise'],
    costBand: 4,
    tags: ['compliance', 'gxp', 'regulated-industries', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://gxpmanager.com' },
  },
  {
    id: 'workato',
    name: 'Workato',
    provider: 'Workato',
    logo: null,
    summary: 'AI-powered integration and automation platform for enterprise workflow orchestration.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'Intelligent Automation',
    features: { conversational: true, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: false, paidPlans: [{ name: 'Team', price: 'Contact', features: 'Core platform' }, { name: 'Enterprise', price: 'Custom', features: 'Full features' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, hipaaCompliant: true, soc2Certified: true, dataLocation: 'Multiple regions' },
    pros: ['1000+ connectors', 'AI-powered recipes', 'Enterprise-grade'],
    cons: ['Premium pricing', 'Learning curve'],
    bestFor: ['Enterprise integration', 'IT automation', 'Cross-app workflows'],
    notIdealFor: ['Simple integrations', 'Budget-constrained'],
    useCases: ['automation'],
    userTypes: ['business'],
    dataSensitivity: ['business', 'personal'],
    technicalLevel: ['some-setup', 'developer'],
    pricingModel: ['enterprise'],
    costBand: 4,
    tags: ['integration', 'automation', 'enterprise', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://www.workato.com' },
  },
  {
    id: 'streamlogic',
    name: 'Streamlogic',
    provider: 'Streamlogic',
    logo: null,
    summary: 'AI-powered data streaming and processing platform for real-time analytics.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'AI-Enabled Platform',
    features: { conversational: false, apiAccess: true },
    pricing: { hasFreeeTier: false, paidPlans: [{ name: 'Enterprise', price: 'Custom', features: 'Streaming platform' }] },
    dataHandling: { level: 'moderate', gdprCompliant: true, dataLocation: 'Cloud' },
    pros: ['Real-time processing', 'AI analytics', 'Scalable'],
    cons: ['Technical focus', 'Enterprise pricing'],
    bestFor: ['Real-time analytics', 'Event processing', 'IoT data'],
    notIdealFor: ['Batch processing', 'Non-technical users'],
    useCases: ['data-analysis', 'automation'],
    userTypes: ['business'],
    dataSensitivity: ['business'],
    technicalLevel: ['developer'],
    pricingModel: ['enterprise'],
    costBand: 3,
    tags: ['data-streaming', 'real-time', 'analytics', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://streamlogic.io' },
  },
  {
    id: 'explo',
    name: 'Explo',
    provider: 'Explo',
    logo: null,
    summary: 'AI-powered embedded analytics platform for building customer-facing dashboards.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'AI-Enabled Platform',
    features: { conversational: true, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: true, paidPlans: [{ name: 'Growth', price: 'From $250/month', features: 'Embedded analytics' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, soc2Certified: true, dataLocation: 'USA' },
    pros: ['Embedded analytics', 'White-label', 'AI insights'],
    cons: ['B2B SaaS focus', 'Requires development'],
    bestFor: ['SaaS products', 'Customer portals', 'Embedded BI'],
    notIdealFor: ['Internal analytics', 'Non-SaaS'],
    useCases: ['data-analysis'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['business'],
    technicalLevel: ['developer'],
    pricingModel: ['free', 'subscription'],
    costBand: 3,
    tags: ['embedded-analytics', 'bi', 'saas', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://explo.co' },
  },
  {
    id: 'ambar',
    name: 'Ambar',
    provider: 'Ambar',
    logo: null,
    summary: 'AI-powered enterprise search platform for finding information across all company data.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'Core AI',
    features: { conversational: true, documentAnalysis: true, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: false, paidPlans: [{ name: 'Enterprise', price: 'Custom', features: 'Enterprise search' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, soc2Certified: true, dataLocation: 'Cloud/On-prem' },
    pros: ['Unified search', 'AI-powered', 'Security-focused'],
    cons: ['Enterprise only', 'Implementation needed'],
    bestFor: ['Knowledge management', 'Enterprise search', 'Information discovery'],
    notIdealFor: ['Small teams', 'Public search'],
    useCases: ['internal-docs'],
    userTypes: ['business'],
    dataSensitivity: ['business', 'personal'],
    technicalLevel: ['some-setup'],
    pricingModel: ['enterprise'],
    costBand: 4,
    tags: ['enterprise-search', 'knowledge-management', 'ai-search', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://ambar.cloud' },
  },
  {
    id: 'sensational-systems',
    name: 'Sensational Systems',
    provider: 'Sensational Systems',
    logo: null,
    summary: 'AI-powered business automation platform for SMB digital transformation.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'Intelligent Automation',
    features: { conversational: false, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: false, paidPlans: [{ name: 'Business', price: 'Contact', features: 'Automation platform' }] },
    dataHandling: { level: 'moderate', gdprCompliant: true, dataLocation: 'Cloud' },
    pros: ['SMB-focused', 'Practical automation', 'UK-based'],
    cons: ['Regional focus', 'Smaller ecosystem'],
    bestFor: ['SMB automation', 'UK businesses', 'Process improvement'],
    notIdealFor: ['Enterprise scale', 'Global deployments'],
    useCases: ['automation'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['business'],
    technicalLevel: ['some-setup'],
    pricingModel: ['subscription'],
    costBand: 2,
    tags: ['smb-automation', 'digital-transformation', 'uk', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://sensationalsystems.co.uk' },
  },
  
  // === D. AI Security, Trust & Risk ===
  {
    id: 'threatspike',
    name: 'ThreatSpike',
    provider: 'ThreatSpike',
    logo: null,
    summary: 'AI-powered threat detection platform for real-time cybersecurity monitoring.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'Core AI',
    features: { conversational: false, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: false, paidPlans: [{ name: 'Enterprise', price: 'Custom', features: 'Threat detection' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, soc2Certified: true, dataLocation: 'Cloud' },
    pros: ['Real-time detection', 'AI-driven', 'SOC integration'],
    cons: ['Security focus only', 'Enterprise pricing'],
    bestFor: ['Security teams', 'SOCs', 'Threat monitoring'],
    notIdealFor: ['Small businesses', 'Non-security'],
    useCases: ['automation'],
    userTypes: ['business'],
    dataSensitivity: ['business', 'regulated'],
    technicalLevel: ['some-setup'],
    pricingModel: ['enterprise'],
    costBand: 3,
    tags: ['threat-detection', 'cybersecurity', 'ai-security', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://threatspike.com' },
  },
  {
    id: 'secvalmsp',
    name: 'Security Validation | SecValMSSP',
    provider: 'SecValMSSP',
    logo: null,
    summary: 'AI-powered security validation and managed security services for continuous testing.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'AI-Enabled Platform',
    features: { conversational: false, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: false, paidPlans: [{ name: 'Managed', price: 'Custom', features: 'Security services' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, soc2Certified: true, dataLocation: 'Cloud' },
    pros: ['Continuous validation', 'Managed services', 'Expert team'],
    cons: ['Service model', 'Not self-service'],
    bestFor: ['Security validation', 'Pen testing', 'Managed security'],
    notIdealFor: ['DIY security', 'Budget-limited'],
    useCases: ['automation'],
    userTypes: ['business'],
    dataSensitivity: ['business', 'regulated'],
    technicalLevel: ['some-setup'],
    pricingModel: ['enterprise'],
    costBand: 3,
    tags: ['security-validation', 'managed-security', 'pen-testing', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://secvalmsp.com' },
  },
  {
    id: 'sekant-security',
    name: 'Sekant Security',
    provider: 'Sekant',
    logo: null,
    summary: 'AI-powered application security platform for code vulnerability detection.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'Core AI',
    features: { conversational: false, codeGeneration: false, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: true, paidPlans: [{ name: 'Pro', price: 'Per developer', features: 'Code scanning' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, soc2Certified: true, dataLocation: 'Cloud' },
    pros: ['AI code analysis', 'CI/CD integration', 'Fast scanning'],
    cons: ['Dev-focused', 'Code access needed'],
    bestFor: ['Development teams', 'DevSecOps', 'Code security'],
    notIdealFor: ['Non-developers', 'Infrastructure security'],
    useCases: ['automation'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['business'],
    technicalLevel: ['developer'],
    pricingModel: ['free', 'subscription'],
    costBand: 2,
    tags: ['appsec', 'code-security', 'devsecops', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://sekant.io' },
  },
  {
    id: 'onfido',
    name: 'Onfido',
    provider: 'Onfido',
    logo: null,
    summary: 'AI-powered identity verification platform for secure customer onboarding.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'Core AI',
    features: { conversational: false, imageAnalysis: true, documentAnalysis: true, apiAccess: true, mobileApp: true },
    pricing: { hasFreeeTier: false, paidPlans: [{ name: 'Pay as you go', price: 'Per verification', features: 'ID verification' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, soc2Certified: true, dataLocation: 'Multiple regions' },
    pros: ['AI document analysis', 'Biometric checks', 'Global coverage'],
    cons: ['Per-transaction cost', 'Verification focus'],
    bestFor: ['KYC/AML', 'Customer onboarding', 'Age verification'],
    notIdealFor: ['Non-verification needs', 'Low volume'],
    useCases: ['automation'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['personal', 'regulated'],
    technicalLevel: ['developer'],
    pricingModel: ['usage-based'],
    costBand: 2,
    tags: ['identity-verification', 'kyc', 'ai-security', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://onfido.com' },
  },
  {
    id: 'integrity-security',
    name: 'Integrity Security Services',
    provider: 'Integrity Security',
    logo: null,
    summary: 'AI-powered security assessment and compliance platform for automotive and IoT industries.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'AI-Enabled Platform',
    features: { conversational: false, documentAnalysis: true, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: false, paidPlans: [{ name: 'Enterprise', price: 'Custom', features: 'Security platform' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, dataLocation: 'Multiple regions' },
    pros: ['Industry-specific', 'Compliance focus', 'IoT expertise'],
    cons: ['Niche markets', 'Enterprise focus'],
    bestFor: ['Automotive', 'IoT security', 'Compliance'],
    notIdealFor: ['General IT security', 'Small businesses'],
    useCases: ['automation'],
    userTypes: ['business'],
    dataSensitivity: ['business', 'regulated'],
    technicalLevel: ['some-setup'],
    pricingModel: ['enterprise'],
    costBand: 4,
    tags: ['automotive-security', 'iot-security', 'compliance', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://integrityss.com' },
  },
  {
    id: 'neuralguard',
    name: 'NeuralGuard',
    provider: 'NeuralGuard',
    logo: null,
    summary: 'AI-powered security platform for protecting machine learning models and AI systems.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'Core AI',
    features: { conversational: false, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: false, paidPlans: [{ name: 'Enterprise', price: 'Custom', features: 'AI security' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, soc2Certified: true, dataLocation: 'Cloud' },
    pros: ['AI model protection', 'Adversarial defense', 'MLOps security'],
    cons: ['Specialized niche', 'Requires ML expertise'],
    bestFor: ['ML teams', 'AI security', 'Model protection'],
    notIdealFor: ['Non-AI applications', 'Traditional security'],
    useCases: ['automation'],
    userTypes: ['business'],
    dataSensitivity: ['business'],
    technicalLevel: ['developer'],
    pricingModel: ['enterprise'],
    costBand: 3,
    tags: ['ai-security', 'mlops', 'model-protection', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://neuralguard.ai' },
  },
  {
    id: 'hackurity',
    name: 'Hackurity',
    provider: 'Hackurity',
    logo: null,
    summary: 'AI-powered automated penetration testing platform for continuous security assessment.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'Core AI',
    features: { conversational: false, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: true, paidPlans: [{ name: 'Pro', price: 'Per scan', features: 'Automated testing' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, dataLocation: 'EU' },
    pros: ['Automated pen testing', 'Continuous scanning', 'EU-based'],
    cons: ['Security focus', 'Technical reports'],
    bestFor: ['Security teams', 'Compliance', 'Vulnerability management'],
    notIdealFor: ['Non-technical users', 'General security'],
    useCases: ['automation'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['business'],
    technicalLevel: ['some-setup', 'developer'],
    pricingModel: ['free', 'usage-based'],
    costBand: 2,
    tags: ['pen-testing', 'vulnerability-scanning', 'security', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://hackurity.io' },
  },
  {
    id: 'penta-security',
    name: 'Penta Security Inc.',
    provider: 'Penta Security',
    logo: null,
    summary: 'AI-powered web application firewall and data security platform.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'AI-Enabled Platform',
    features: { conversational: false, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: false, paidPlans: [{ name: 'Enterprise', price: 'Custom', features: 'WAF & security' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, soc2Certified: true, dataLocation: 'Multiple regions' },
    pros: ['AI-powered WAF', 'Data encryption', 'Global presence'],
    cons: ['Enterprise focus', 'Complex setup'],
    bestFor: ['Web security', 'Data protection', 'Enterprise applications'],
    notIdealFor: ['Small websites', 'Budget-limited'],
    useCases: ['automation'],
    userTypes: ['business'],
    dataSensitivity: ['business', 'regulated'],
    technicalLevel: ['some-setup', 'developer'],
    pricingModel: ['enterprise'],
    costBand: 3,
    tags: ['waf', 'data-security', 'encryption', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://pentasecurity.com' },
  },
  {
    id: 'easydmarc',
    name: 'EasyDMARC',
    provider: 'EasyDMARC',
    logo: null,
    summary: 'AI-powered email security platform for DMARC, SPF, and DKIM management.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'AI-Enabled Platform',
    features: { conversational: false, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: true, paidPlans: [{ name: 'Plus', price: '$50/month', features: 'Advanced features' }, { name: 'Business', price: '$200/month', features: 'Full platform' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, soc2Certified: true, dataLocation: 'Cloud' },
    pros: ['Email authentication', 'Phishing protection', 'Easy setup'],
    cons: ['Email focus only', 'Domain knowledge needed'],
    bestFor: ['Email security', 'Brand protection', 'Anti-phishing'],
    notIdealFor: ['Non-email security', 'Small senders'],
    useCases: ['automation'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['business'],
    technicalLevel: ['some-setup'],
    pricingModel: ['free', 'subscription'],
    costBand: 2,
    tags: ['email-security', 'dmarc', 'anti-phishing', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://easydmarc.com' },
  },
  {
    id: 'darkinvader',
    name: 'DarkInvader Limited',
    provider: 'DarkInvader',
    logo: null,
    summary: 'AI-powered dark web monitoring and threat intelligence platform.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'Core AI',
    features: { conversational: false, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: false, paidPlans: [{ name: 'Professional', price: 'Contact', features: 'Dark web monitoring' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, dataLocation: 'UK/EU' },
    pros: ['Dark web intelligence', 'Threat monitoring', 'UK-based'],
    cons: ['Specialized use case', 'Enterprise focus'],
    bestFor: ['Threat intelligence', 'Brand protection', 'Security teams'],
    notIdealFor: ['General security', 'Small businesses'],
    useCases: ['automation'],
    userTypes: ['business'],
    dataSensitivity: ['business'],
    technicalLevel: ['some-setup'],
    pricingModel: ['subscription', 'enterprise'],
    costBand: 3,
    tags: ['dark-web', 'threat-intelligence', 'monitoring', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://darkinvader.io' },
  },
  
  // === E. AI-Enabled SaaS & Enterprise Platforms ===
  {
    id: 'writer',
    name: 'Writer',
    provider: 'Writer',
    logo: null,
    summary: 'Enterprise AI writing platform for creating on-brand content with governance.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'Core AI',
    features: { conversational: true, codeGeneration: false, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: true, paidPlans: [{ name: 'Team', price: '$18/user/month', features: 'Core features' }, { name: 'Enterprise', price: 'Custom', features: 'Full platform' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, soc2Certified: true, hipaaCompliant: true, dataLocation: 'USA' },
    pros: ['Brand governance', 'Style guides', 'Enterprise security'],
    cons: ['Writing focus only', 'Enterprise pricing'],
    bestFor: ['Enterprise writing', 'Marketing teams', 'Brand compliance'],
    notIdealFor: ['Personal use', 'Creative writing'],
    useCases: ['writing-content'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['business'],
    technicalLevel: ['simple'],
    pricingModel: ['free', 'subscription', 'enterprise'],
    costBand: 2,
    tags: ['ai-writing', 'content-generation', 'brand-governance', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://writer.com' },
  },
  {
    id: 'equifax',
    name: 'Equifax',
    provider: 'Equifax',
    logo: null,
    summary: 'AI-powered data analytics and credit intelligence platform for business decisions.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'AI-Enabled Platform',
    features: { conversational: false, documentAnalysis: true, apiAccess: true },
    pricing: { hasFreeeTier: false, paidPlans: [{ name: 'Enterprise', price: 'Volume-based', features: 'Data services' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, soc2Certified: true, dataLocation: 'Multiple regions' },
    pros: ['Extensive data', 'Credit intelligence', 'Global coverage'],
    cons: ['Financial focus', 'Complex pricing'],
    bestFor: ['Credit decisions', 'Risk assessment', 'Identity verification'],
    notIdealFor: ['Non-financial needs', 'Small businesses'],
    useCases: ['data-analysis', 'automation'],
    userTypes: ['business'],
    dataSensitivity: ['personal', 'regulated'],
    technicalLevel: ['some-setup', 'developer'],
    pricingModel: ['usage-based', 'enterprise'],
    costBand: 3,
    tags: ['credit-data', 'risk-assessment', 'identity', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://www.equifax.com' },
  },
  {
    id: 'transunion',
    name: 'TransUnion',
    provider: 'TransUnion',
    logo: null,
    summary: 'AI-powered consumer insights and risk management platform for data-driven decisions.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'AI-Enabled Platform',
    features: { conversational: false, documentAnalysis: true, apiAccess: true },
    pricing: { hasFreeeTier: false, paidPlans: [{ name: 'Enterprise', price: 'Volume-based', features: 'Data platform' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, soc2Certified: true, dataLocation: 'Multiple regions' },
    pros: ['Consumer insights', 'Fraud prevention', 'Marketing data'],
    cons: ['Financial focus', 'Complex integration'],
    bestFor: ['Risk management', 'Marketing', 'Fraud prevention'],
    notIdealFor: ['Non-data needs', 'Small volumes'],
    useCases: ['data-analysis', 'automation'],
    userTypes: ['business'],
    dataSensitivity: ['personal', 'regulated'],
    technicalLevel: ['some-setup', 'developer'],
    pricingModel: ['usage-based', 'enterprise'],
    costBand: 3,
    tags: ['consumer-data', 'risk-management', 'fraud-prevention', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://www.transunion.com' },
  },
  {
    id: 'sandgrain',
    name: 'Sandgrain',
    provider: 'Sandgrain',
    logo: null,
    summary: 'AI-powered spend analytics and procurement optimization platform.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'AI-Enabled Platform',
    features: { conversational: false, documentAnalysis: true, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: false, paidPlans: [{ name: 'Business', price: 'Contact', features: 'Spend analytics' }] },
    dataHandling: { level: 'moderate', gdprCompliant: true, dataLocation: 'Cloud' },
    pros: ['Spend visibility', 'Cost savings', 'Procurement insights'],
    cons: ['Procurement focus', 'Data integration needed'],
    bestFor: ['Procurement teams', 'Finance', 'Cost optimization'],
    notIdealFor: ['Non-procurement', 'Small businesses'],
    useCases: ['data-analysis', 'automation'],
    userTypes: ['business'],
    dataSensitivity: ['business'],
    technicalLevel: ['some-setup'],
    pricingModel: ['subscription', 'enterprise'],
    costBand: 3,
    tags: ['spend-analytics', 'procurement', 'cost-optimization', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://sandgrain.com' },
  },
  {
    id: 'zylo',
    name: 'Zylo',
    provider: 'Zylo',
    logo: null,
    summary: 'AI-powered SaaS management platform for optimizing software spend and licenses.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'AI-Enabled Platform',
    features: { conversational: false, documentAnalysis: true, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: false, paidPlans: [{ name: 'Enterprise', price: 'Custom', features: 'SaaS management' }] },
    dataHandling: { level: 'moderate', gdprCompliant: true, soc2Certified: true, dataLocation: 'Cloud' },
    pros: ['SaaS discovery', 'License optimization', 'Renewal management'],
    cons: ['Enterprise focus', 'Financial access needed'],
    bestFor: ['IT teams', 'Finance', 'SaaS governance'],
    notIdealFor: ['Small businesses', 'Non-SaaS'],
    useCases: ['data-analysis', 'automation'],
    userTypes: ['business'],
    dataSensitivity: ['business'],
    technicalLevel: ['some-setup'],
    pricingModel: ['enterprise'],
    costBand: 3,
    tags: ['saas-management', 'license-optimization', 'finops', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://zylo.com' },
  },
  {
    id: 'coveo',
    name: 'Coveo',
    provider: 'Coveo',
    logo: null,
    summary: 'AI-powered relevance platform for enterprise search and personalized experiences.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'Core AI',
    features: { conversational: true, documentAnalysis: true, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: false, paidPlans: [{ name: 'Pro', price: 'Contact', features: 'Search platform' }, { name: 'Enterprise', price: 'Custom', features: 'Full platform' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, soc2Certified: true, hipaaCompliant: true, dataLocation: 'Multiple regions' },
    pros: ['AI relevance', 'Personalization', 'Enterprise-grade'],
    cons: ['Complex implementation', 'Premium pricing'],
    bestFor: ['Enterprise search', 'E-commerce', 'Customer service'],
    notIdealFor: ['Simple search', 'Small websites'],
    useCases: ['internal-docs', 'customer-support'],
    userTypes: ['business'],
    dataSensitivity: ['business', 'personal'],
    technicalLevel: ['some-setup', 'developer'],
    pricingModel: ['enterprise'],
    costBand: 4,
    tags: ['enterprise-search', 'personalization', 'ai-relevance', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://www.coveo.com' },
  },
  {
    id: 'fruggr',
    name: 'Fruggr',
    provider: 'Fruggr',
    logo: null,
    summary: 'AI-powered digital sustainability platform for measuring and reducing tech carbon footprint.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'AI-Enabled Platform',
    features: { conversational: false, apiAccess: true, teamCollaboration: true },
    pricing: { hasFreeeTier: true, paidPlans: [{ name: 'Pro', price: 'Contact', features: 'Full analytics' }] },
    dataHandling: { level: 'moderate', gdprCompliant: true, dataLocation: 'EU' },
    pros: ['Sustainability metrics', 'Carbon tracking', 'ESG reporting'],
    cons: ['Niche focus', 'Newer platform'],
    bestFor: ['ESG reporting', 'Sustainability teams', 'Green IT'],
    notIdealFor: ['Non-sustainability needs', 'Small footprints'],
    useCases: ['data-analysis'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['business'],
    technicalLevel: ['simple', 'some-setup'],
    pricingModel: ['free', 'subscription'],
    costBand: 2,
    tags: ['sustainability', 'carbon-footprint', 'esg', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://fruggr.io' },
  },
  {
    id: 'slurm',
    name: 'Slurm (SchedMD)',
    provider: 'SchedMD',
    logo: null,
    summary: 'AI-ready workload manager for high-performance computing and ML training clusters.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'AI-Enabled Platform',
    features: { conversational: false, apiAccess: true },
    pricing: { hasFreeeTier: true, paidPlans: [{ name: 'Support', price: 'Contact', features: 'Enterprise support' }] },
    dataHandling: { level: 'clear', dataLocation: 'On-premise' },
    pros: ['Open source', 'HPC standard', 'ML/AI ready'],
    cons: ['Technical expertise needed', 'Infrastructure required'],
    bestFor: ['HPC clusters', 'ML training', 'Research computing'],
    notIdealFor: ['Non-technical users', 'Cloud-only'],
    useCases: ['automation'],
    userTypes: ['business'],
    dataSensitivity: ['business'],
    technicalLevel: ['developer'],
    pricingModel: ['free', 'enterprise'],
    costBand: 2,
    tags: ['hpc', 'workload-management', 'ml-training', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://slurm.schedmd.com' },
  },
  {
    id: 'nextcloud',
    name: 'Nextcloud',
    provider: 'Nextcloud',
    logo: null,
    summary: 'AI-enabled collaboration platform with on-premise file sync, sharing, and productivity.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'AI-Enabled Platform',
    features: { conversational: true, documentAnalysis: true, apiAccess: true, mobileApp: true, teamCollaboration: true },
    pricing: { hasFreeeTier: true, paidPlans: [{ name: 'Enterprise', price: 'Per user/year', features: 'Full support' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, hipaaCompliant: true, dataLocation: 'Self-hosted' },
    pros: ['Self-hosted', 'Data sovereignty', 'AI assistant'],
    cons: ['Infrastructure needed', 'Self-management'],
    bestFor: ['Data sovereignty', 'Privacy-focused', 'Self-hosting'],
    notIdealFor: ['Cloud-only needs', 'No IT resources'],
    useCases: ['internal-docs', 'automation'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['business', 'personal', 'regulated'],
    technicalLevel: ['some-setup', 'developer'],
    pricingModel: ['free', 'subscription', 'enterprise'],
    costBand: 2,
    tags: ['collaboration', 'self-hosted', 'file-sync', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://nextcloud.com' },
  },
  {
    id: 'exadel',
    name: 'EXADEL',
    provider: 'Exadel',
    logo: null,
    summary: 'AI and ML development services and CompreFace open-source face recognition platform.',
    description: 'This is an initial profile created for discoverability. Further verification and assessment will be added over time.',
    aiClassification: 'Core AI',
    features: { conversational: false, imageAnalysis: true, apiAccess: true },
    pricing: { hasFreeeTier: true, paidPlans: [{ name: 'Enterprise', price: 'Custom', features: 'Development services' }] },
    dataHandling: { level: 'clear', gdprCompliant: true, dataLocation: 'Customer choice' },
    pros: ['Open source options', 'Face recognition', 'Custom development'],
    cons: ['Service model', 'Technical focus'],
    bestFor: ['AI development', 'Face recognition', 'Custom ML'],
    notIdealFor: ['Ready-made solutions', 'Non-technical users'],
    useCases: ['automation', 'image-media'],
    userTypes: ['small-team', 'business'],
    dataSensitivity: ['business', 'personal'],
    technicalLevel: ['developer'],
    pricingModel: ['free', 'enterprise'],
    costBand: 3,
    tags: ['ai-development', 'face-recognition', 'open-source', 'techex-wave2'],
    sourceTag: 'TechEx Global 2026 – Wave 2',
    links: { website: 'https://exadel.com' },
  },
];

// Feature display configuration
export const featureConfig = {
  conversational: { label: 'Conversational AI', category: 'Core' },
  codeGeneration: { label: 'Code Generation', category: 'Core' },
  imageGeneration: { label: 'Image Generation', category: 'Media' },
  imageAnalysis: { label: 'Image Analysis', category: 'Media' },
  documentAnalysis: { label: 'Document Analysis', category: 'Documents' },
  webBrowsing: { label: 'Web Browsing', category: 'Data' },
  apiAccess: { label: 'API Access', category: 'Technical' },
  customInstructions: { label: 'Custom Instructions', category: 'Customisation' },
  pluginsExtensions: { label: 'Plugins/Extensions', category: 'Customisation' },
  voiceInput: { label: 'Voice Input', category: 'Input' },
  mobileApp: { label: 'Mobile App', category: 'Platform' },
  teamCollaboration: { label: 'Team Collaboration', category: 'Collaboration' },
};

// Data confidence levels for comparison view
// Indicates source reliability for each data point
export const confidenceLevels = {
  vendor: {
    label: 'Vendor-provided',
    description: 'Information from official vendor documentation',
    color: 'text-primary',
  },
  reviewed: {
    label: 'Independently reviewed',
    description: 'Verified through independent testing or third-party sources',
    color: 'text-success',
  },
  unclear: {
    label: 'Information unclear',
    description: 'Data could not be independently verified',
    color: 'text-warning',
  },
};

// Metadata about each comparison field - what's factual vs interpretive
export const fieldMetadata = {
  // Factual fields (from vendor documentation)
  name: { type: 'fact', confidence: 'vendor' },
  provider: { type: 'fact', confidence: 'vendor' },
  summary: { type: 'fact', confidence: 'vendor' },
  costBand: { type: 'fact', confidence: 'vendor' },
  hasFreeeTier: { type: 'fact', confidence: 'vendor' },
  paidPlans: { type: 'fact', confidence: 'vendor' },
  apiPricing: { type: 'fact', confidence: 'vendor' },
  dataLocation: { type: 'fact', confidence: 'vendor' },
  gdprCompliant: { type: 'fact', confidence: 'vendor' },
  hipaaCompliant: { type: 'fact', confidence: 'vendor' },
  soc2Certified: { type: 'fact', confidence: 'vendor' },
  
  // Interpretive fields (our assessment based on research)
  bestFor: { type: 'interpretation', confidence: 'reviewed' },
  notIdealFor: { type: 'interpretation', confidence: 'reviewed' },
  pros: { type: 'interpretation', confidence: 'reviewed' },
  cons: { type: 'interpretation', confidence: 'reviewed' },
  suitability: { type: 'interpretation', confidence: 'reviewed' },
  
  // Features - mostly vendor-provided
  features: { type: 'fact', confidence: 'vendor' },
  
  // Data handling assessments
  dataHandlingLevel: { type: 'interpretation', confidence: 'reviewed' },
  optOutAvailable: { type: 'fact', confidence: 'vendor' },
  enterpriseDataProtection: { type: 'fact', confidence: 'vendor' },
};

// Question configuration for wizard
export const wizardQuestions = [
  {
    id: 'purpose',
    step: 1,
    title: 'What do you want to use AI for?',
    subtitle: 'Select all that apply to your needs',
    type: 'multi-select',
    options: [
      { id: 'customer-support', label: 'Customer support / chat', icon: 'MessageCircle' },
      { id: 'writing-content', label: 'Writing & content', icon: 'PenTool' },
      { id: 'internal-docs', label: 'Internal documents / knowledge', icon: 'FileText' },
      { id: 'automation', label: 'Automation & workflows', icon: 'Zap' },
      { id: 'data-analysis', label: 'Data analysis', icon: 'BarChart3' },
      { id: 'image-media', label: 'Image or media creation', icon: 'Image' },
      { id: 'not-sure', label: "I'm not sure yet", icon: 'HelpCircle' },
    ],
  },
  {
    id: 'userType',
    step: 2,
    title: 'Who will be using this AI?',
    subtitle: 'This helps us understand the scale of your needs',
    type: 'single-select',
    options: [
      { id: 'individual', label: 'Just me', icon: 'User' },
      { id: 'small-team', label: 'Small team', icon: 'Users' },
      { id: 'business', label: 'Whole business', icon: 'Building2' },
      { id: 'public', label: 'Customers / public users', icon: 'Globe' },
    ],
  },
  {
    id: 'dataSensitivity',
    step: 3,
    title: 'How sensitive is the data you\'ll use?',
    subtitle: 'Different platforms have different security features',
    helperText: 'Hover or tap the info icon for examples.',
    type: 'single-select',
    options: [
      { 
        id: 'public', 
        label: 'Public / low sensitivity', 
        icon: 'Unlock',
        tooltip: 'Information that is already public or not private, such as general marketing content, public website text, or non-confidential ideas.'
      },
      { 
        id: 'business', 
        label: 'Business data', 
        icon: 'Briefcase',
        tooltip: 'Internal business information such as documents, emails, processes, or strategy that isn\'t public but doesn\'t include personal or regulated data.'
      },
      { 
        id: 'personal', 
        label: 'Personal data', 
        icon: 'UserCheck',
        tooltip: 'Information about identifiable people, such as names, contact details, customer messages, or employee information.'
      },
      { 
        id: 'regulated', 
        label: 'Highly sensitive or regulated data', 
        icon: 'Shield',
        tooltip: 'Data that is legally protected or high-risk, such as health records, financial details, legal documents, or government-regulated information.'
      },
      { 
        id: 'not-sure', 
        label: 'Not sure', 
        icon: 'HelpCircle',
        tooltip: 'If you\'re unsure, we\'ll show options that are more cautious by default.'
      },
    ],
  },
  {
    id: 'technicalLevel',
    step: 4,
    title: 'How technical do you want this to be?',
    subtitle: 'We\'ll match you with platforms that fit your comfort level',
    helperText: 'Hover or tap the info icon for examples.',
    type: 'single-select',
    options: [
      { 
        id: 'simple', 
        label: 'Very simple (plug-and-play)', 
        icon: 'Sparkles',
        tooltip: 'Works out of the box with little or no setup. Typically accessed through a web app or simple interface. No technical knowledge required.'
      },
      { 
        id: 'some-setup', 
        label: 'Some setup is okay', 
        icon: 'Settings',
        tooltip: 'May require light configuration or integrations. Suitable if you\'re comfortable following basic setup instructions.'
      },
      { 
        id: 'developer', 
        label: 'Technical / developer-ready', 
        icon: 'Code',
        tooltip: 'Designed for technical users. May require coding, APIs, or infrastructure setup. Offers more flexibility and control, but requires technical expertise.'
      },
    ],
  },
  {
    id: 'budget',
    step: 5,
    title: 'What kind of budget are you expecting?',
    subtitle: 'This helps narrow down pricing options',
    type: 'single-select',
    options: [
      { id: 'free', label: 'Free / very low cost', icon: 'Gift' },
      { id: 'subscription', label: 'Monthly subscription', icon: 'CreditCard' },
      { id: 'usage-based', label: 'Usage-based pricing', icon: 'Activity' },
      { id: 'enterprise', label: 'Enterprise / flexible', icon: 'Building' },
      { id: 'not-sure', label: 'Not sure', icon: 'HelpCircle' },
    ],
  },
];

// Suitability matching logic
export const calculateSuitability = (platform, answers) => {
  let score = 0;
  let maxScore = 0;
  const reasons = {
    suitable: [],
    concerns: [],
  };

  // Safely get arrays with fallbacks
  const platformUseCases = platform.useCases || [];
  const platformUserTypes = platform.userTypes || [];
  const platformDataSensitivity = platform.dataSensitivity || [];
  const platformTechnicalLevel = platform.technicalLevel || [];
  const platformPricingModel = platform.pricingModel || [];

  // Purpose matching (multi-select)
  if (answers.purpose && answers.purpose.length > 0) {
    const purposeMatches = answers.purpose.filter(
      p => p === 'not-sure' || platformUseCases.includes(p)
    );
    
    if (answers.purpose.includes('not-sure')) {
      score += 1;
      maxScore += 1;
    } else {
      maxScore += answers.purpose.length * 2;
      score += purposeMatches.length * 2;
      
      if (purposeMatches.length === answers.purpose.length) {
        reasons.suitable.push('Covers all your selected use cases');
      } else if (purposeMatches.length > 0) {
        reasons.suitable.push(`Supports ${purposeMatches.length} of your ${answers.purpose.length} selected uses`);
        reasons.concerns.push('May not cover all your needs');
      } else {
        reasons.concerns.push('Not designed for your selected use cases');
      }
    }
  }

  // User type matching
  if (answers.userType) {
    maxScore += 3;
    if (platformUserTypes.includes(answers.userType)) {
      score += 3;
      reasons.suitable.push(`Suitable for ${answers.userType === 'individual' ? 'individual use' : answers.userType === 'small-team' ? 'small teams' : answers.userType === 'business' ? 'business-wide deployment' : 'public-facing applications'}`);
    } else {
      reasons.concerns.push('May not be ideal for your user scale');
    }
  }

  // Data sensitivity matching
  if (answers.dataSensitivity && answers.dataSensitivity !== 'not-sure') {
    maxScore += 4;
    if (platformDataSensitivity.includes(answers.dataSensitivity)) {
      score += 4;
      if (answers.dataSensitivity === 'regulated') {
        reasons.suitable.push('Designed to handle sensitive/regulated data');
      } else {
        reasons.suitable.push('Appropriate data handling for your needs');
      }
    } else {
      if (answers.dataSensitivity === 'regulated') {
        reasons.concerns.push('May not meet requirements for regulated data');
      } else {
        reasons.concerns.push('Data handling may not match your sensitivity level');
      }
    }
  }

  // Technical level matching
  if (answers.technicalLevel) {
    maxScore += 3;
    if (platformTechnicalLevel.includes(answers.technicalLevel)) {
      score += 3;
      if (answers.technicalLevel === 'simple') {
        reasons.suitable.push('Easy to get started without technical knowledge');
      } else if (answers.technicalLevel === 'developer') {
        reasons.suitable.push('Developer-friendly with API access');
      }
    } else {
      if (answers.technicalLevel === 'simple' && !platformTechnicalLevel.includes('simple')) {
        reasons.concerns.push('May require more technical setup than expected');
      }
    }
  }

  // Budget matching
  if (answers.budget && answers.budget !== 'not-sure') {
    maxScore += 3;
    if (platformPricingModel.includes(answers.budget)) {
      score += 3;
      reasons.suitable.push('Pricing model matches your budget expectations');
    } else {
      reasons.concerns.push('Pricing structure may not align with your budget');
    }
  }

  // Calculate percentage
  const percentage = maxScore > 0 ? (score / maxScore) * 100 : 50;

  // Determine suitability level
  let suitability;
  if (percentage >= 70) {
    suitability = 'suitable';
  } else if (percentage >= 40) {
    suitability = 'caution';
  } else {
    suitability = 'not-recommended';
  }

  return {
    suitability,
    score,
    maxScore,
    percentage,
    reasons,
  };
};

// Helper to get platform by ID
export const getPlatformById = (id) => {
  return platforms.find(p => p.id === id);
};
